import React, { useEffect, useState } from "react";
import "./App.css";

const BASE_URL = (() => {
  if (window.__ENV__?.API_URL && window.__ENV__.API_URL !== '%REACT_APP_API_URL%') {
    return window.__ENV__.API_URL;
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  return "http://127.0.0.1:8000";
})();

const BOT_USERNAME = (() => {
  if (
    window.__ENV__?.BOT_USERNAME &&
    window.__ENV__.BOT_USERNAME !== '%REACT_APP_BOT_USERNAME%'
  ) {
    return window.__ENV__.BOT_USERNAME;
  }
  if (process.env.REACT_APP_BOT_USERNAME) {
    return process.env.REACT_APP_BOT_USERNAME;
  }
  return "";
})();

if (process.env.NODE_ENV === 'development') {
  console.log('BASE_URL:', BASE_URL);
  console.log('window.__ENV__:', window.__ENV__);
  console.log('process.env.REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
  console.log('process.env.REACT_APP_BOT_USERNAME:', process.env.REACT_APP_BOT_USERNAME);
}

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const buildEventLink = (eventId) => {
  if (!eventId || !BOT_USERNAME) return "";
  const username = String(BOT_USERNAME).replace(/^@/, "");
  const safeId = encodeURIComponent(String(eventId));
  return `https://t.me/${username}?start=${safeId}`;
};

const copyToClipboard = async (text) => {
  if (!text) return false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fallback below
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const FormattingHint = () => (
  <p className="hint">
    Поддерживается простая разметка, которая на сервере конвертируется в HTML
    (parse_mode=&quot;HTML&quot;):{" "}
    <code>**жирный**</code>,{" "}
    <code>*курсив*</code> или <code>_курсив_</code>,{" "}
    <code>__подчёркнутый__</code>,{" "}
    <code>~~зачёркнутый~~</code>,{" "}
    <code>`код`</code>,{" "}
    <code>[текст](https://example.com)</code> — ссылка.
  </p>
);

function Modal({ title, children, onClose }) {
  if (!onClose) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="icon-button" onClick={onClose}>
            Г—
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

function App() {
  const [token, setToken] = useState("");
  const [authStatus, setAuthStatus] = useState("pending"); // "pending" | "ok" | "error" | "no-init"
  const [activeTab, setActiveTab] = useState("users");
  const [loading, setLoading] = useState(false);
  const [lastResponse, setLastResponse] = useState(null);
  const [lastError, setLastError] = useState(null);
  const [lastAction, setLastAction] = useState("");

  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }
  const [modal, setModal] = useState(null); // { title, content }

  // авто-скрытие тоста
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(id);
  }, [toast]);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const apiRequest = async (
    path,
    {
      method = "GET",
      body,
      isFormData = false,
      needsAuth = false,
      successMessage, // string | false | undefined
    } = {}
  ) => {
    setLoading(true);
    setLastError(null);

    try {
      const headers = {};
      if (!isFormData) {
        headers["Content-Type"] = "application/json";
      }
      if (needsAuth && token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const res = await fetch(`${BASE_URL}${path}`, {
        method,
        headers,
        body: isFormData
          ? body
          : body !== undefined
            ? JSON.stringify(body)
            : undefined,
      });

      const text = await res.text();
      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = text;
      }

      if (!res.ok) {
        const error = { status: res.status, data };
        setLastError(error);
        setLastResponse(null);
        showToast("Ошибка при запросе к серверу", "error");
        throw error;
      }

      setLastResponse(data);

      // уведомления по умолчанию — для всех успешных запросов,
      // кроме тех, где successMessage === false
      if (successMessage !== false) {
        showToast(successMessage || "Запрос успешно выполнен", "success");
      }

      return data;
    } finally {
      setLoading(false);
    }
  };

  const apiDownloadBlob = async (path, { needsAuth = false } = {}) => {
    setLoading(true);
    setLastError(null);
    try {
      const headers = {};
      if (needsAuth && token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const res = await fetch(`${BASE_URL}${path}`, { method: "GET", headers });
      if (!res.ok) {
        const text = await res.text();
        let data = text;
        try {
          data = text ? JSON.parse(text) : null;
        } catch {
          // ignore
        }
        const error = { status: res.status, data };
        setLastError(error);
        setLastResponse(null);
        showToast("Ошибка при запросе к серверу", "error");
        throw error;
      }
      const blob = await res.blob();
      const contentDisposition = res.headers.get("Content-Disposition") || "";
      return { blob, contentDisposition };
    } finally {
      setLoading(false);
    }
  };

  // Авторизация по initData из Telegram WebApp
  useEffect(() => {
    const initData =
      (window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.initData) ||
      "";

    if (!initData) {
      setAuthStatus("no-init");
      return;
    }

    setAuthStatus("pending");

    apiRequest("/api/admin/auth/", {
      method: "POST",
      body: { init_data: initData },
      needsAuth: false,
      successMessage: false, // тут отдельно покажем тост ниже
    })
      .then((data) => {
        if (data && data.access_token) {
          setToken(data.access_token);
          setAuthStatus("ok");
          showToast("Успешная авторизация", "success");
        } else {
          setAuthStatus("error");
        }
      })
      .catch(() => {
        setAuthStatus("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderAuthStatus = () => {
    if (authStatus === "pending") {
      return <span className="badge badge-neutral">Авторизация…</span>;
    }
    if (authStatus === "ok") {
      return <span className="badge badge-ok">Админ</span>;
    }
    if (authStatus === "no-init") {
      return (
        <span className="badge badge-warn">
          Нет initData — запустите через Telegram WebApp
        </span>
      );
    }
    if (authStatus === "error") {
      return (
        <span className="badge badge-error">
          Ошибка авторизации. Проверьте запуск из Telegram.
        </span>
      );
    }
    return null;
  };

  const isAuthed = authStatus === "ok" && Boolean(token);

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-left">
          <h1 className="app-title">Админ-панель бота</h1>
          <p className="app-subtitle">
            Простое управление пользователями, воронками и поддержкой.
          </p>
        </div>
        <div className="header-right">{renderAuthStatus()}</div>
      </header>

      <div className="main-layout">
        <aside className="sidebar">
          <button
            className={`tab-button ${activeTab === "users" ? "active" : ""}`}
            onClick={() => setActiveTab("users")}
            disabled={!isAuthed}
          >
            Пользователи
          </button>
          <button
            className={`tab-button ${activeTab === "funnels" ? "active" : ""}`}
            onClick={() => setActiveTab("funnels")}
            disabled={!isAuthed}
          >
            Воронки
          </button>
          <button
            className={`tab-button ${activeTab === "events" ? "active" : ""}`}
            onClick={() => setActiveTab("events")}
            disabled={!isAuthed}
          >
            Мероприятия
          </button>
          <button
            className={`tab-button ${activeTab === "support" ? "active" : ""}`}
            onClick={() => setActiveTab("support")}
            disabled={!isAuthed}
          >
            Поддержка
          </button>

          <div className="sidebar-section">
            <button
              className="secondary-btn full-width"
              onClick={() => {
                setLastAction("Проверка openapi.json");
                apiRequest("/openapi.json", {
                  method: "GET",
                  successMessage: "OpenAPI загружен",
                });
              }}
            >
              Проверить API
            </button>
          </div>
        </aside>

        <main className="content">
          {!isAuthed ? (
            <section className="data-block">
              <h2 className="section-title">Нужна авторизация</h2>
              <p className="section-description">
                Данные загрузятся автоматически после авторизации (кнопку
                «Обновить» нажимать не нужно).
              </p>
            </section>
          ) : (
            <>
              {activeTab === "users" && (
                <UsersSection
                  apiRequest={apiRequest}
                  setLastAction={setLastAction}
                />
              )}
              {activeTab === "funnels" && (
                <FunnelsSection
                  apiRequest={apiRequest}
                  setLastAction={setLastAction}
                />
              )}
              {activeTab === "events" && (
                <EventsSection
                  apiRequest={apiRequest}
                  apiDownloadBlob={apiDownloadBlob}
                  setLastAction={setLastAction}
                  showToast={showToast}
                />
              )}
              {activeTab === "support" && (
                <SupportSection
                  apiRequest={apiRequest}
                  setLastAction={setLastAction}
                />
              )}
            </>
          )}

          {isAuthed && activeTab !== "support" && (
          <section className="response-panel">
            <div className="response-header">
              <div className="response-header-left">
                <h2>Ответ сервера</h2>
                {lastAction && (
                  <span className="last-action">
                    Действие: <span>{lastAction}</span>
                  </span>
                )}
              </div>
              {loading && (
                <span className="badge badge-neutral">Загрузка…</span>
              )}
            </div>

            {lastError && (
              <div className="error-box">
                <div className="error-title">
                  Ошибка {lastError.status || ""} при запросе
                </div>
                <pre className="json-output">
                  {JSON.stringify(lastError.data, null, 2)}
                </pre>
              </div>
            )}

            {!lastError && lastResponse !== null && (
              <>
                <p className="muted">
                  Основные данные уже показаны в таблицах и блоках выше. Если
                  нужно — можно открыть сырой JSON-ответ.
                </p>
                <button
                  type="button"
                  className="secondary-btn small-btn"
                  onClick={() =>
                    setModal({
                      title: "Сырой ответ сервера",
                      content: (
                        <pre className="json-output modal-json">
                          {JSON.stringify(lastResponse, null, 2)}
                        </pre>
                      ),
                    })
                  }
                >
                  Показать сырой JSON
                </button>
              </>
            )}

            {!lastError && lastResponse === null && !loading && (
              <p className="muted">
                Здесь можно посмотреть техническую информацию по последнему
                запросу.
              </p>
            )}
          </section>
          )}
        </main>
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`}>{toast.message}</div>
      )}

      {modal && (
        <Modal title={modal.title} onClose={() => setModal(null)}>
          {modal.content}
        </Modal>
      )}
    </div>
  );
}

// ======== USERS SECTION ========

function UsersSection({ apiRequest, setLastAction }) {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [adminUserId, setAdminUserId] = useState("");
  const [adminUserIdDelete, setAdminUserIdDelete] = useState("");
  const [admins, setAdmins] = useState([]);
  const [adminsSearchQuery, setAdminsSearchQuery] = useState("");
  const [adminsLoading, setAdminsLoading] = useState(false);

  const USERS_PER_PAGE = 20;

  // Загрузка пользователей с пагинацией
  const loadUsers = async (page = 1) => {
    setLoading(true);
    try {
      setLastAction("Загрузка пользователей");
      const offset = (page - 1) * USERS_PER_PAGE;
      const data = await apiRequest(
        `/api/admin/user/?limit=${USERS_PER_PAGE}&offset=${offset}`,
        {
          method: "GET",
          needsAuth: true,
          successMessage: false,
        }
      );

      if (data && data.users) {
        setUsers(data.users);
        setTotalCount(data.total_count || 0);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Ошибка загрузки пользователей:", error);
    } finally {
      setLoading(false);
    }
  };

  // Загрузка администраторов
  const loadAdmins = async () => {
    setAdminsLoading(true);
    try {
      setLastAction("Загрузка администраторов");
      const data = await apiRequest("/api/telegram/user/admins", {
        method: "GET",
        needsAuth: true,
        successMessage: false,
      });

      if (Array.isArray(data)) {
        setAdmins(data);
      } else if (Array.isArray(data?.admins)) {
        setAdmins(data.admins);
      }
    } catch (error) {
      console.error("Ошибка загрузки администраторов:", error);
    } finally {
      setAdminsLoading(false);
    }
  };

  // Автоматическая загрузка при монтировании
  useEffect(() => {
    loadUsers(1);
    loadAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Поиск по пользователям (по текущей странице)
  const filteredUsers = searchQuery
    ? users.filter(
      (u) =>
        String(u.id).includes(searchQuery) ||
        (u.user_name && u.user_name.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    : users;

  // Поиск по администраторам
  const filteredAdmins = adminsSearchQuery
    ? admins.filter(
      (u) =>
        String(u.id).includes(adminsSearchQuery) ||
        (u.user_name && u.user_name.toLowerCase().includes(adminsSearchQuery.toLowerCase()))
    )
    : admins;

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!adminUserId) return;
    setLastAction("Добавить администратора");
    await apiRequest(`/api/admin/user/${adminUserId}/admin`, {
      method: "PUT",
      needsAuth: true,
      successMessage: "Администратор добавлен",
    });
    // Обновляем списки после добавления
    loadUsers(currentPage);
    loadAdmins();
    setAdminUserId("");
  };

  const handleRemoveAdmin = async (e) => {
    e.preventDefault();
    if (!adminUserIdDelete) return;
    setLastAction("Удалить администратора");
    await apiRequest(`/api/admin/user/${adminUserIdDelete}/admin`, {
      method: "DELETE",
      needsAuth: true,
      successMessage: "Администратор удалён",
    });
    // Обновляем списки после удаления
    loadUsers(currentPage);
    loadAdmins();
    setAdminUserIdDelete("");
  };

  const handleRefresh = () => {
    loadUsers(currentPage);
    loadAdmins();
  };

  const handleNextPage = () => {
    const nextPage = currentPage + 1;
    const maxPage = Math.ceil(totalCount / USERS_PER_PAGE);
    if (nextPage <= maxPage) {
      loadUsers(nextPage);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      loadUsers(currentPage - 1);
    }
  };

  const renderUserRow = (u) => {
    const avatarUrl = u.user_name
      ? `https://t.me/i/userpic/160/${u.user_name}.jpg`
      : null;

    return (
      <div className="table-row" key={u.id}>
        <div>{u.id}</div>
        <div>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={u.user_name}
              className="avatar"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <span className="muted">—</span>
          )}
        </div>
        <div>{u.user_name ? `@${u.user_name}` : "без username"}</div>
        <div>{u.role}</div>
        <div>{u.segment_membership ?? "—"}</div>
      </div>
    );
  };

  const totalPages = Math.ceil(totalCount / USERS_PER_PAGE);

  return (
    <>
      <h2 className="section-title">Пользователи</h2>
      <p className="section-description">
        Просмотр пользователей, админов и изменение прав доступа.
      </p>

      {/* Блок всех пользователей */}
      <div className="data-block">
        <div className="data-block-header">
          <h3 className="data-title">Все пользователи</h3>
          <div className="data-block-controls">
            <input
              type="text"
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по ID или username..."
            />
            <button
              onClick={handleRefresh}
              className="secondary-btn small-btn"
              disabled={loading}
            >
              {loading ? "Загрузка..." : "Обновить"}
            </button>
          </div>
        </div>

        {loading && currentPage === 1 && (
          <p className="hint">Загрузка пользователей...</p>
        )}

        {!loading && filteredUsers.length === 0 && (
          <p className="hint">Пользователи не найдены</p>
        )}

        {filteredUsers.length > 0 && (
          <>
            <div className="table users-table">
              <div className="table-row table-header">
                <div>ID</div>
                <div>Аватар</div>
                <div>Имя</div>
                <div>Роль</div>
                <div>Сегмент</div>
              </div>
              {filteredUsers.map(renderUserRow)}
            </div>

            {!searchQuery && (
              <div className="pagination">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1 || loading}
                  className="secondary-btn small-btn"
                >
                  ← Назад
                </button>
                <span className="pagination-info">
                  Страница {currentPage} из {totalPages} (всего: {totalCount})
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages || loading}
                  className="secondary-btn small-btn"
                >
                  Вперёд →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Блок администраторов */}
      <div className="data-block">
        <div className="data-block-header">
          <h3 className="data-title">Администраторы</h3>
          <div className="data-block-controls">
            <input
              type="text"
              className="search-input"
              value={adminsSearchQuery}
              onChange={(e) => setAdminsSearchQuery(e.target.value)}
              placeholder="Поиск по ID или username..."
            />
            <button
              onClick={loadAdmins}
              className="secondary-btn small-btn"
              disabled={adminsLoading}
            >
              {adminsLoading ? "Загрузка..." : "Обновить"}
            </button>
          </div>
        </div>

        {adminsLoading && (
          <p className="hint">Загрузка администраторов...</p>
        )}

        {!adminsLoading && filteredAdmins.length === 0 && (
          <p className="hint">Администраторы не найдены</p>
        )}

        {filteredAdmins.length > 0 && (
          <div className="table users-table">
            <div className="table-row table-header">
              <div>ID</div>
              <div>Аватар</div>
              <div>Имя</div>
              <div>Роль</div>
              <div>Сегмент</div>
            </div>
            {filteredAdmins.map(renderUserRow)}
          </div>
        )}
      </div>

      {/* Управление администраторами */}
      <div className="card-grid">
        <section className="card">
          <h3 className="card-title">Добавить админа</h3>
          <form onSubmit={handleAddAdmin} className="form">
            <div className="form-row">
              <label className="form-label">
                Telegram ID
                <input
                  type="number"
                  className="form-input"
                  value={adminUserId}
                  onChange={(e) => setAdminUserId(e.target.value)}
                  required
                  placeholder="Введите ID пользователя"
                />
              </label>
            </div>
            <button type="submit" className="primary-btn">
              Добавить
            </button>
          </form>
        </section>

        <section className="card">
          <h3 className="card-title">Удалить админа</h3>
          <form onSubmit={handleRemoveAdmin} className="form">
            <div className="form-row">
              <label className="form-label">
                Telegram ID
                <input
                  type="number"
                  className="form-input"
                  value={adminUserIdDelete}
                  onChange={(e) => setAdminUserIdDelete(e.target.value)}
                  required
                  placeholder="Введите ID администратора"
                />
              </label>
            </div>
            <button type="submit" className="danger-btn">
              Удалить
            </button>
          </form>
        </section>
      </div>
    </>
  );
}

// ======== FUNNELS SECTION ========

function FunnelsSection({ apiRequest, setLastAction }) {
  // ===== Данные =====
  const [funnelsList, setFunnelsList] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [allAnswerOptions, setAllAnswerOptions] = useState([]);
  const [segmentsList, setSegmentsList] = useState([]);
  const [segmentRolesList, setSegmentRolesList] = useState([]);
  const [scheduledBroadcasts, setScheduledBroadcasts] = useState([]);
  const [loading, setLoading] = useState(false);

  // ===== Создание сущностей =====
  const [funnelTitle, setFunnelTitle] = useState("");
  const [funnelType, setFunnelType] = useState("default"); // default | initial | menu

  const [selectedFunnelForQuestion, setSelectedFunnelForQuestion] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [questionDelay, setQuestionDelay] = useState("");
  const [questionNextFunnelId, setQuestionNextFunnelId] = useState("");
  const [questionFiles, setQuestionFiles] = useState([]);

  const [selectedQuestionForButton, setSelectedQuestionForButton] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [buttonType, setButtonType] = useState("buttons");
  const [buttonUrl, setButtonUrl] = useState("");
  const [selectedNextQuestion, setSelectedNextQuestion] = useState("");

  const [segmentName, setSegmentName] = useState("");
  const [selectedSegmentForRule, setSelectedSegmentForRule] = useState("");
  const [selectedAnswerForSegment, setSelectedAnswerForSegment] = useState("");

  const [selectedFunnelForStart, setSelectedFunnelForStart] = useState("");
  const [selectedSegmentForStart, setSelectedSegmentForStart] = useState("");
  const [startSentAtLocal, setStartSentAtLocal] = useState("");
  const [isTestBroadcast, setIsTestBroadcast] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingBroadcastData, setPendingBroadcastData] = useState(null);
  const [editingBroadcast, setEditingBroadcast] = useState(null);
  const [newBroadcastTime, setNewBroadcastTime] = useState("");

  // Модалка удаления воронки
  const [showDeleteFunnelDialog, setShowDeleteFunnelDialog] = useState(false);
  const [funnelToDelete, setFunnelToDelete] = useState(null);

  // ===== Редактирование =====
  const [funnelEditor, setFunnelEditor] = useState(null);
  const [isFunnelEditorDirty, setIsFunnelEditorDirty] = useState(false);
  const [questionEditor, setQuestionEditor] = useState(null);
  const [buttonEditor, setButtonEditor] = useState(null);

  // ===== Фильтры и пагинация =====
  const [funnelSearch, setFunnelSearch] = useState("");
  const [funnelsPage, setFunnelsPage] = useState(1);
  const funnelsPerPage = 6;

  const [questionsPage, setQuestionsPage] = useState(1);
  const questionsPerPage = 5;

  const [buttonsPage, setButtonsPage] = useState(1);
  const buttonsPerPage = 5;

  const [segmentsPage, setSegmentsPage] = useState(1);
  const segmentsPerPage = 6;

  const [scheduledUpcomingPage, setScheduledUpcomingPage] = useState(1);
  const [scheduledHistoryPage, setScheduledHistoryPage] = useState(1);
  const scheduledPerPage = 5;

  const paginate = (items, page, perPage) => {
    if (!Array.isArray(items) || perPage <= 0) return [];
    const start = (page - 1) * perPage;
    return items.slice(start, start + perPage);
  };

  const getTotalPages = (count, perPage) =>
    Math.max(1, Math.ceil(Math.max(count, 1) / perPage));

  const PaginationControls = ({ page, totalPages, onPrev, onNext }) => (
    <div className="pagination-controls compact">
      <button
        type="button"
        className="pagination-button"
        onClick={onPrev}
        disabled={page <= 1}
      >
        ←
      </button>
      <span className="pagination-info">
        {page} / {totalPages}
      </span>
      <button
        type="button"
        className="pagination-button"
        onClick={onNext}
        disabled={page >= totalPages}
      >
        →
      </button>
    </div>
  );

  // Безопасное превью текста вопроса (учитываем null / пустой текст)
  const getQuestionPreview = (q, maxLen = 60) => {
    if (!q || !q.text) {
      return "(без текста)";
    }
    const text = String(q.text);
    if (text.length <= maxLen) {
      return text;
    }
    return text.substring(0, maxLen) + "...";
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      setLastAction("Загрузка данных");
      const [funnelsResp, segmentsResp, segmentRulesResp, scheduledResp] = await Promise.all([
        apiRequest("/api/telegram/funnel?limit=200&offset=0", {
          method: "GET",
          needsAuth: true,
          successMessage: false,
        }),
        apiRequest("/api/admin/segment/?limit=200&offset=0", {
          method: "GET",
          needsAuth: true,
          successMessage: false,
        }),
        apiRequest("/api/admin/segment/role?limit=500&offset=0", {
          method: "GET",
          needsAuth: true,
          successMessage: false,
        }),
        apiRequest("/api/telegram/funnel/scheduled/info", {
          method: "GET",
          needsAuth: true,
          successMessage: false,
        }),
      ]);

      let funnels = [];
      if (Array.isArray(funnelsResp)) {
        funnels = funnelsResp;
      } else if (funnelsResp?.funnels) {
        funnels = funnelsResp.funnels;
      }
      setFunnelsList(funnels);

      if (funnels.length > 0) {
        const questionsPromises = funnels.map((funnel) =>
          apiRequest(`/api/telegram/funnel/${funnel.id}`, {
            method: "GET",
            needsAuth: true,
            successMessage: false,
          }).catch(() => [])
        );
        const questionsResults = await Promise.all(questionsPromises);
        const allQuestionsFlat = questionsResults.flat();
        setAllQuestions(allQuestionsFlat);

        const allButtons = allQuestionsFlat.flatMap((q) =>
          (q.options || []).map((opt) => ({ ...opt, question_id: q.id, question_text: q.text }))
        );
        setAllAnswerOptions(allButtons);
      } else {
        setAllQuestions([]);
        setAllAnswerOptions([]);
      }

      setSegmentsList(Array.isArray(segmentsResp) ? segmentsResp : []);
      setSegmentRolesList(Array.isArray(segmentRulesResp) ? segmentRulesResp : []);
      setScheduledBroadcasts(Array.isArray(scheduledResp) ? scheduledResp : []);
    } catch (error) {
      console.error("Ошибка загрузки данных:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const questionsForSelectedFunnel = selectedFunnelForQuestion
    ? allQuestions.filter((q) => q.funnel_id === Number(selectedFunnelForQuestion))
    : [];

  const questionsSorted = [...questionsForSelectedFunnel].sort(
    (a, b) => a.order_index - b.order_index
  );
  const paginatedQuestions = paginate(questionsSorted, questionsPage, questionsPerPage);
  const questionsTotalPages = getTotalPages(questionsSorted.length || 1, questionsPerPage);

  const buttonsForSelectedQuestion = selectedQuestionForButton
    ? allAnswerOptions.filter((opt) => opt.question_id === Number(selectedQuestionForButton))
    : [];
  const buttonsSorted = [...buttonsForSelectedQuestion];
  const paginatedButtons = paginate(buttonsSorted, buttonsPage, buttonsPerPage);
  const buttonsTotalPages = getTotalPages(buttonsSorted.length || 1, buttonsPerPage);

  const currentQuestionForButtons = allQuestions.find(
    (q) => q.id === Number(selectedQuestionForButton)
  );

  // Все сообщения из всех воронок, с названием воронки, сортировка по убыванию funnel_id
  const questionsForButtonsSelectAll = allQuestions
    .map((q) => {
      const funnel = funnelsList.find((f) => f.id === q.funnel_id);
      return {
        ...q,
        funnelTitle: funnel ? funnel.title : `Воронка #${q.funnel_id}`,
      };
    })
    .sort((a, b) => {
      // сначала по убыванию funnel_id
      if (a.funnel_id !== b.funnel_id) return b.funnel_id - a.funnel_id;
      // внутри воронки по order_index
      return (a.order_index || 0) - (b.order_index || 0);
    });

  // Для выбора сообщения, к которому прикрепляем кнопку — только сообщения выбранной воронки
  const questionsForButtonsSelect = selectedFunnelForQuestion
    ? questionsForButtonsSelectAll.filter(
      (q) => q.funnel_id === Number(selectedFunnelForQuestion)
    )
    : [];

  // Для выбора "следующего сообщения" — из всех воронок,
  // но сначала сообщения из текущей воронки, затем остальные
  const nextQuestionsOptions = currentQuestionForButtons
    ? questionsForButtonsSelectAll
      .slice()
      .sort((a, b) => {
        const currentFunnelId = currentQuestionForButtons.funnel_id;
        const aSame = a.funnel_id === currentFunnelId;
        const bSame = b.funnel_id === currentFunnelId;

        if (aSame && !bSame) return -1;
        if (!aSame && bSame) return 1;

        if (a.funnel_id !== b.funnel_id) return b.funnel_id - a.funnel_id;
        return (a.order_index || 0) - (b.order_index || 0);
      })
    : [];

  const filteredFunnels = funnelsList.filter((funnel) =>
    funnel.title.toLowerCase().includes(funnelSearch.trim().toLowerCase())
  );
  const paginatedFunnels = paginate(filteredFunnels, funnelsPage, funnelsPerPage);
  const funnelsTotalPages = getTotalPages(filteredFunnels.length || 1, funnelsPerPage);

  const segmentsWithRules = segmentsList.map((segment) => ({
    ...segment,
    rules: segmentRolesList
      .filter((rule) => rule.segment_id === segment.id)
      .map((rule) => {
        const answerOption = allAnswerOptions.find((opt) => opt.id === rule.answer_id);
        const question = answerOption
          ? allQuestions.find((q) => q.id === answerOption.question_id)
          : null;
        const funnel = question
          ? funnelsList.find((f) => f.id === question.funnel_id)
          : null;
        return {
          ...rule,
          answerOption,
          question,
          funnel,
        };
      }),
  }));
  const paginatedSegments = paginate(segmentsWithRules, segmentsPage, segmentsPerPage);
  const segmentsTotalPages = getTotalPages(segmentsWithRules.length || 1, segmentsPerPage);

  const upcomingBroadcasts = scheduledBroadcasts.filter(
    (item) => item.status === "scheduled" || (item.status === "in_progress" && item.send_at)
  );
  const completedBroadcasts = scheduledBroadcasts.filter(
    (item) => !item.active && Boolean(item.end_time)
  );
  const paginatedUpcoming = paginate(upcomingBroadcasts, scheduledUpcomingPage, scheduledPerPage);
  const paginatedHistory = paginate(completedBroadcasts, scheduledHistoryPage, scheduledPerPage);
  const upcomingTotalPages = getTotalPages(upcomingBroadcasts.length || 1, scheduledPerPage);
  const historyTotalPages = getTotalPages(completedBroadcasts.length || 1, scheduledPerPage);

  useEffect(() => {
    if (!selectedFunnelForQuestion) {
      setFunnelEditor(null);
      setIsFunnelEditorDirty(false);
      return;
    }
    const funnel = funnelsList.find(
      (f) => String(f.id) === String(selectedFunnelForQuestion)
    );
    if (!funnel) return;
    if (isFunnelEditorDirty) return;

    // Тип воронки: обычная / начальная / команда меню
    let type = "default";
    if (funnel.initial) {
      type = "initial";
    } else if (funnel.start_command) {
      type = "menu";
    }

    setFunnelEditor({
      id: funnel.id,
      title: funnel.title,
      type,
    });
  }, [selectedFunnelForQuestion, funnelsList, isFunnelEditorDirty]);

  useEffect(() => {
    setFunnelsPage(1);
  }, [funnelSearch]);

  useEffect(() => {
    setQuestionsPage(1);
    setButtonsPage(1);
    setQuestionEditor(null);
    setButtonEditor(null);
    setSelectedQuestionForButton("");
  }, [selectedFunnelForQuestion]);

  useEffect(() => {
    setButtonsPage(1);
    setButtonEditor(null);
  }, [selectedQuestionForButton]);

  const handleQuestionFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    // ограничиваем до 10 файлов
    const limited = files.slice(0, 10);
    setQuestionFiles(limited);
  };

  const removePendingFile = (fileName) => {
    setQuestionFiles((prev) => prev.filter((file) => file.name !== fileName));
  };

  const handleCreateFunnel = async (e) => {
    e.preventDefault();
    setLastAction("Создать воронку");

    // Маппинг типов:
    // default       -> initial=false, start_command=false
    // initial       -> initial=true,  start_command=false
    // menu (команда меню) -> initial=false, start_command=true
    const body = {
      title: funnelTitle,
      initial: funnelType === "initial",
      start_command: funnelType === "menu",
    };

    const created = await apiRequest("/api/telegram/funnel/create", {
      method: "POST",
      needsAuth: true,
      body,
      successMessage: "Воронка создана",
    });
    if (created?.id) {
      setFunnelTitle("");
      setFunnelType("default");
      await loadAllData();
    }
  };

  const handleSelectFunnel = (value) => {
    setSelectedFunnelForQuestion(value);
    setIsFunnelEditorDirty(false);
  };

  const handleDeleteFunnelClick = (funnel) => {
    setFunnelToDelete(funnel);
    setShowDeleteFunnelDialog(true);
  };

  const confirmDeleteFunnel = async () => {
    if (!funnelToDelete) return;

    setLastAction("Удалить воронку");

    await apiRequest(`/api/telegram/funnel/delete/${funnelToDelete.id}`, {
      method: "DELETE",
      needsAuth: true,
      successMessage: "Воронка удалена",
    });

    // если удаляем выбранную — сбрасываем выбор
    if (String(selectedFunnelForQuestion) === String(funnelToDelete.id)) {
      setSelectedFunnelForQuestion("");
      setFunnelEditor(null);
    }

    setShowDeleteFunnelDialog(false);
    setFunnelToDelete(null);
    await loadAllData();
  };

  const cancelDeleteFunnel = () => {
    setShowDeleteFunnelDialog(false);
    setFunnelToDelete(null);
  };

  const handleUpdateFunnelSettings = async (e) => {
    e.preventDefault();
    if (!funnelEditor) return;
    setLastAction("Обновить воронку");

    let initial = false;
    let startCommand = false;

    if (funnelEditor.type === "initial") {
      initial = true;
    } else if (funnelEditor.type === "menu") {
      startCommand = true;
    }

    const payload = {
      title: funnelEditor.title,
      initial,
      start_command: startCommand,
    };

    await apiRequest(`/api/telegram/funnel/${funnelEditor.id}`, {
      method: "PATCH",
      needsAuth: true,
      body: payload,
      successMessage: "Воронка обновлена",
    });
    setIsFunnelEditorDirty(false);
    await loadAllData();
  };

  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    setLastAction("Создать сообщение");
    const formData = new FormData();
    formData.append("funnel_id", selectedFunnelForQuestion);

    if (questionText.trim()) {
      formData.append("text", questionText);
    }

    if (questionDelay) {
      formData.append("delay_before_next_sec", String(Number(questionDelay)));
    }

    if (questionNextFunnelId) {
      formData.append("next_funnel_id", String(Number(questionNextFunnelId)));
    }

    questionFiles.forEach((file) => {
      formData.append("files", file);
    });

    const created = await apiRequest("/api/telegram/funnel/create/question", {
      method: "POST",
      needsAuth: true,
      isFormData: true,
      body: formData,
      successMessage: "Сообщение создано",
    });
    if (created?.id) {
      setQuestionText("");
      setQuestionDelay("");
      setQuestionNextFunnelId("");
      setQuestionFiles([]);
      await loadAllData();
    }
  };

  const startEditQuestion = (question) => {
    setQuestionEditor({
      id: question.id,
      text: question.text,
      order_index: question.order_index,
      delay_before_next_sec: question.delay_before_next_sec ?? "",
      next_funnel_id: question.next_funnel_id ?? "",
      existingFiles: question.files || [],
      filesToDelete: [],
      newFiles: [],
    });
    setSelectedQuestionForButton(String(question.id));
  };

  const toggleQuestionEditorFile = (fileName) => {
    setQuestionEditor((prev) => {
      if (!prev) return prev;
      const exists = prev.filesToDelete.includes(fileName);
      return {
        ...prev,
        filesToDelete: exists
          ? prev.filesToDelete.filter((f) => f !== fileName)
          : [...prev.filesToDelete, fileName],
      };
    });
  };

  const handleQuestionEditorNewFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setQuestionEditor((prev) => {
      if (!prev) return prev;

      const existingCount = (prev.existingFiles || []).length - (prev.filesToDelete || []).length;
      const allowed = Math.max(0, 10 - existingCount);
      const limitedNew = files.slice(0, allowed);

      return {
        ...prev,
        newFiles: limitedNew,
      };
    });
  };

  const removeQuestionEditorNewFile = (fileName) => {
    setQuestionEditor((prev) =>
      prev
        ? {
          ...prev,
          newFiles: prev.newFiles.filter((file) => file.name !== fileName),
        }
        : prev
    );
  };

  const handleSubmitQuestionEdit = async (e) => {
    e.preventDefault();
    if (!questionEditor) return;
    setLastAction("Обновить сообщение");
    const formData = new FormData();
    if (questionEditor.text) {
      formData.append("text", questionEditor.text);
    }
    if (typeof questionEditor.order_index !== "undefined") {
      formData.append("order_index", String(questionEditor.order_index));
    }

    if (
      questionEditor.delay_before_next_sec !== undefined &&
      questionEditor.delay_before_next_sec !== ""
    ) {
      formData.append(
        "delay_before_next_sec",
        String(questionEditor.delay_before_next_sec)
      );
    }

    if (
      questionEditor.next_funnel_id !== undefined &&
      questionEditor.next_funnel_id !== ""
    ) {
      formData.append("next_funnel_id", String(questionEditor.next_funnel_id));
    }

    if (questionEditor.filesToDelete.length > 0) {
      formData.append("files_to_delete", questionEditor.filesToDelete.join(","));
    }
    questionEditor.newFiles.forEach((file) => formData.append("files", file));

    await apiRequest(`/api/telegram/funnel/question/${questionEditor.id}`, {
      method: "PATCH",
      needsAuth: true,
      isFormData: true,
      body: formData,
      successMessage: "Сообщение обновлено",
    });
    setQuestionEditor(null);
    await loadAllData();
  };

  const handleCreateButton = async (e) => {
    e.preventDefault();
    setLastAction("Создать кнопку");
    const body = {
      question_id: Number(selectedQuestionForButton),
      text: buttonText,
      answer_type: buttonType,
      next_question_id: selectedNextQuestion ? Number(selectedNextQuestion) : 0,
    };
    if (buttonUrl.trim()) {
      body.url = buttonUrl.trim();
    }
    await apiRequest("/api/telegram/funnel/create/answer-options", {
      method: "POST",
      needsAuth: true,
      body,
      successMessage: "Кнопка создана",
    });
    setButtonText("");
    setButtonUrl("");
    setSelectedNextQuestion("");
    await loadAllData();
  };

  const startEditButton = (button) => {
    setButtonEditor({
      id: button.id,
      text: button.text,
      answer_type: button.answer_type || button.type || "buttons",
      url: button.url || "",
      next_question_id: button.next_question_id || 0,
    });
  };

  const handleSubmitButtonEdit = async (e) => {
    e.preventDefault();
    if (!buttonEditor) return;
    setLastAction("Обновить кнопку");
    const payload = {
      text: buttonEditor.text,
      answer_type: buttonEditor.answer_type,
      url: buttonEditor.url || null,
      next_question_id: Number(buttonEditor.next_question_id) || 0,
    };
    await apiRequest(`/api/telegram/funnel/answer-options/${buttonEditor.id}`, {
      method: "PATCH",
      needsAuth: true,
      body: payload,
      successMessage: "Кнопка обновлена",
    });
    setButtonEditor(null);
    await loadAllData();
  };

  const handleCreateSegment = async (e) => {
    e.preventDefault();
    setLastAction("Создать сегмент");
    await apiRequest("/api/admin/segment/", {
      method: "POST",
      needsAuth: true,
      body: { name: segmentName },
      successMessage: "Сегмент создан",
    });
    setSegmentName("");
    setSegmentsPage(1);
    await loadAllData();
  };

  const handleCreateSegmentRule = async (e) => {
    e.preventDefault();
    setLastAction("Создать правило сегмента");
    await apiRequest("/api/admin/segment/role", {
      method: "POST",
      needsAuth: true,
      body: {
        segment_id: Number(selectedSegmentForRule),
        answer_id: Number(selectedAnswerForSegment),
      },
      successMessage: "Правило создано",
    });
    setSelectedSegmentForRule("");
    setSelectedAnswerForSegment("");
    await loadAllData();
  };

  const handleStartFunnel = async (e) => {
    e.preventDefault();

    const body = {
      funnel_id: Number(selectedFunnelForStart),
      test: isTestBroadcast,
    };
    if (selectedSegmentForStart) {
      body.by_segment = Number(selectedSegmentForStart);
    }
    if (startSentAtLocal) {
      const localDate = new Date(startSentAtLocal);
      body.sent_at = localDate.toISOString();
    }

    if (!isTestBroadcast && !startSentAtLocal) {
      setPendingBroadcastData(body);
      setShowConfirmDialog(true);
      return;
    }

    await executeBroadcast(body);
  };

  const executeBroadcast = async (body) => {
    setLastAction("Запустить воронку");
    await apiRequest("/api/telegram/funnel/start-funnel", {
      method: "POST",
      needsAuth: true,
      body,
      successMessage: isTestBroadcast ? "Тестовая рассылка отправлена" : "Воронка запущена",
    });
    setShowConfirmDialog(false);
    setPendingBroadcastData(null);
    setIsTestBroadcast(false);
    setSelectedFunnelForStart("");
    setSelectedSegmentForStart("");
    setStartSentAtLocal("");
    await loadAllData();
  };

  const handleCancelBroadcast = async (broadcastId) => {
    if (!window.confirm("Вы уверены, что хотите отменить эту рассылку?")) {
      return;
    }
    setLastAction("Отменить рассылку");
    await apiRequest(`/api/telegram/funnel/scheduled/${broadcastId}`, {
      method: "DELETE",
      needsAuth: true,
      successMessage: "Рассылка отменена",
    });
    await loadAllData();
  };

  const handleUpdateBroadcastTime = async (broadcastId) => {
    if (!newBroadcastTime) {
      alert("Введите новое время отправки");
      return;
    }
    setLastAction("Изменить время рассылки");
    const localDate = new Date(newBroadcastTime);
    await apiRequest(`/api/telegram/funnel/scheduled/${broadcastId}`, {
      method: "PATCH",
      needsAuth: true,
      body: {
        new_send_at: localDate.toISOString(),
      },
      successMessage: "Время рассылки изменено",
    });
    setEditingBroadcast(null);
    setNewBroadcastTime("");
    await loadAllData();
  };

  return (
    <>
      <div className="data-block-header" style={{ marginBottom: "16px" }}>
        <h2 className="section-title">Воронки</h2>
        <button
          onClick={loadAllData}
          className="secondary-btn small-btn"
          disabled={loading}
        >
          {loading ? "Загрузка..." : "Обновить"}
        </button>
      </div>
      <p className="section-description">
        Последовательный конструктор: создаём воронку → сообщения → кнопки → сегменты → запуск.
      </p>

      {/* Шаг 1. Создание и список воронок */}
      <div className="card-grid">
        <section className="card">
          <h3 className="card-title">1. Создать воронку</h3>
          <form onSubmit={handleCreateFunnel} className="form">
            <div className="form-row">
              <label className="form-label">
                Название
                <input
                  className="form-input"
                  value={funnelTitle}
                  onChange={(e) => setFunnelTitle(e.target.value)}
                  required
                  placeholder="Название воронки"
                />
              </label>
            </div>
            <div className="form-row">
              <label className="form-label">
                Тип
                <select
                  className="form-input"
                  value={funnelType}
                  onChange={(e) => setFunnelType(e.target.value)}
                >
                  <option value="default">Обычная</option>
                  <option value="initial">Начальный сценарий</option>
                  <option value="menu">Команда меню</option>
                </select>
              </label>
            </div>
            <button type="submit" className="primary-btn">
              Сохранить
            </button>
          </form>
        </section>
        <section className="card">
          <div className="card-title-row">
            <h3 className="card-title">Список воронок</h3>
            <input
              className="search-input"
              placeholder="Поиск по названию"
              value={funnelSearch}
              onChange={(e) => setFunnelSearch(e.target.value)}
            />
          </div>
          {paginatedFunnels.length === 0 ? (
            <p className="hint">Воронки не найдены</p>
          ) : (
            <>
              <div className="table funnels-table">
                <div className="table-row table-header">
                  <div>ID</div>
                  <div>Название</div>
                  <div>Начальная</div>
                  <div>Команда меню</div>
                  <div></div>
                </div>
                {paginatedFunnels.map((funnel) => {
                  const isSelected =
                    String(selectedFunnelForQuestion) === String(funnel.id);
                  return (
                    <div
                      className={`table-row ${isSelected ? "row-selected" : ""}`}
                      key={funnel.id}
                    >
                      <div>{funnel.id}</div>
                      <div>{funnel.title}</div>
                      <div>{funnel.initial ? "Да" : "—"}</div>
                      <div>{funnel.start_command ? "Да" : "—"}</div>
                      <div className="table-cell-actions">
                        <button
                          type="button"
                          className="ghost-btn"
                          onClick={() => handleSelectFunnel(String(funnel.id))}
                        >
                          Выбрать
                        </button>
                        <span className="action-separator">|</span>
                        <button
                          type="button"
                          className="ghost-btn danger-ghost"
                          onClick={() => handleDeleteFunnelClick(funnel)}
                        >
                          Удалить
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <PaginationControls
                page={funnelsPage}
                totalPages={funnelsTotalPages}
                onPrev={() => setFunnelsPage((prev) => Math.max(1, prev - 1))}
                onNext={() =>
                  setFunnelsPage((prev) => Math.min(funnelsTotalPages, prev + 1))
                }
              />
            </>
          )}
        </section>
      </div>

      {/* Шаг 2. Настройки выбранной воронки */}
      {selectedFunnelForQuestion && funnelEditor && (
        <div className="data-block">
          <h3 className="data-title">2. Настройки выбранной воронки</h3>
          <form onSubmit={handleUpdateFunnelSettings} className="form">
            <div className="form-row">
              <label className="form-label">
                Название
                <input
                  className="form-input"
                  value={funnelEditor.title}
                  onChange={(e) => {
                    setFunnelEditor((prev) => ({ ...prev, title: e.target.value }));
                    setIsFunnelEditorDirty(true);
                  }}
                  required
                />
              </label>
            </div>
            <div className="form-row">
              <label className="form-label">
                Тип
                <select
                  className="form-input"
                  value={funnelEditor.type}
                  onChange={(e) => {
                    setFunnelEditor((prev) => ({ ...prev, type: e.target.value }));
                    setIsFunnelEditorDirty(true);
                  }}
                >
                  <option value="default">Обычная</option>
                  <option value="initial">Начальная</option>
                  <option value="menu">Команда меню</option>
                </select>
              </label>
            </div>
            <button type="submit" className="secondary-btn" disabled={!isFunnelEditorDirty}>
              Обновить
            </button>
            <button
              type="button"
              className="danger-btn"
              onClick={() =>
                handleDeleteFunnelClick({
                  id: funnelEditor.id,
                  title: funnelEditor.title,
                })
              }
            >
              Удалить воронку
            </button>
          </form>
        </div>
      )}

      {/* Шаг 3. Сообщения */}
      <div className="data-block">
        <h3 className="data-title">3. Сообщения выбранной воронки</h3>
        <form onSubmit={handleCreateQuestion} className="form">
          <div className="form-row">
            <label className="form-label">
              Воронка
              <select
                className="form-input"
                value={selectedFunnelForQuestion}
                onChange={(e) => handleSelectFunnel(e.target.value)}
              >
                <option value="">Выберите воронку</option>
                {funnelsList.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.title}
                    {f.initial && " (Начальная)"}
                    {f.start_command && " (Команда меню)"}
                  </option>
                ))}
              </select>
            </label>
            <p className="hint">
              Если оставить пустым — рассылка отправится сразу.
            </p>
          </div>
          {selectedFunnelForQuestion && (
            <>
              <div className="form-row">
                <label className="form-label">
                  Текст сообщения (необязательно)
                  <textarea
                    className="form-textarea"
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    placeholder="Текст сообщения или оставьте пустым, если только файлы"
                  />
                </label>
                <FormattingHint />
              </div>

              <div className="form-row">
                <label className="form-label">
                  Задержка перед запуском следующей воронки (секунды, опционально)
                  <input
                    type="number"
                    className="form-input"
                    value={questionDelay}
                    onChange={(e) => setQuestionDelay(e.target.value)}
                    placeholder="Например, 3600 для 1 часа"
                    min="0"
                  />
                </label>
              </div>

              <div className="form-row">
                <label className="form-label">
                  Следующая воронка (опционально)
                  <select
                    className="form-input"
                    value={questionNextFunnelId}
                    onChange={(e) => setQuestionNextFunnelId(e.target.value)}
                  >
                    <option value="">Не запускать другую воронку автоматически</option>
                    {funnelsList
                      .filter((f) => f.id !== Number(selectedFunnelForQuestion))
                      .map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.title}
                        </option>
                      ))}
                  </select>
                </label>
                <p className="hint">
                  Если указать задержку и следующую воронку, после этого сообщения через
                  указанное время запустится новая воронка.
                </p>
              </div>

              <div className="form-row">
                <label className="form-label">
                  Файлы (до 10, опционально)
                  <input
                    type="file"
                    multiple
                    className="form-file"
                    onChange={handleQuestionFilesChange}
                  />
                </label>
                {questionFiles.length > 0 && (
                  <div className="file-preview-list">
                    {questionFiles.map((file) => (
                      <span className="pill" key={file.name}>
                        {file.name}
                        <button
                          type="button"
                          className="pill-action"
                          onClick={() => removePendingFile(file.name)}
                        >
                          Г—
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="hint">
                  Telegram отправляет до 10 медиа в одном альбоме. Здесь можно прикрепить до 10
                  файлов к одному сообщению.
                </p>
              </div>
              <button type="submit" className="primary-btn">
                Сохранить сообщение
              </button>
            </>
          )}
        </form>

        {selectedFunnelForQuestion && (
          <>
            {paginatedQuestions.length === 0 ? (
              <p className="hint" style={{ marginTop: "12px" }}>
                Воронка ещё пустая
              </p>
            ) : (
              <div className="table" style={{ marginTop: "16px" }}>
                <div className="table-row table-header">
                  <div>#</div>
                  <div>Текст</div>
                  <div>Файлы</div>
                  <div>Задержка (сек)</div>
                  <div>ID следующей воронки</div>
                  <div></div>
                </div>
                {paginatedQuestions.map((question) => (
                  <div className="table-row" key={question.id}>
                    <div>{question.order_index}</div>
                    <div>{question.text || "(без текста)"}</div>
                    <div>{question.files?.length || "—"}</div>
                    <div>{question.delay_before_next_sec ?? "—"}</div>
                    <div>{question.next_funnel_id ?? "—"}</div>
                    <div>
                      <button
                        type="button"
                        className="ghost-btn"
                        onClick={() => startEditQuestion(question)}
                      >
                        Редактировать
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {questionsSorted.length > questionsPerPage && (
              <PaginationControls
                page={questionsPage}
                totalPages={questionsTotalPages}
                onPrev={() => setQuestionsPage((prev) => Math.max(1, prev - 1))}
                onNext={() =>
                  setQuestionsPage((prev) => Math.min(questionsTotalPages, prev + 1))
                }
              />
            )}
          </>
        )}
      </div>

      {questionEditor && (
        <div className="card editor-card">
          <div className="editor-header">
            <h4>Редактировать сообщение #{questionEditor.id}</h4>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setQuestionEditor(null)}
            >
              Отмена
            </button>
          </div>
          <form onSubmit={handleSubmitQuestionEdit} className="form">
            <div className="form-row">
              <label className="form-label">
                Текст
                <textarea
                  className="form-textarea"
                  value={questionEditor.text || ""}
                  onChange={(e) =>
                    setQuestionEditor((prev) => ({ ...prev, text: e.target.value }))
                  }
                />
              </label>
              <FormattingHint />
            </div>
            <div className="form-row">
              <label className="form-label">
                Порядок
                <input
                  type="number"
                  className="form-input"
                  value={questionEditor.order_index}
                  onChange={(e) =>
                    setQuestionEditor((prev) => ({
                      ...prev,
                      order_index: Number(e.target.value),
                    }))
                  }
                  required
                />
              </label>
            </div>

            <div className="form-row">
              <label className="form-label">
                Задержка перед запуском следующей воронки (секунды, опционально)
                <input
                  type="number"
                  className="form-input"
                  value={questionEditor.delay_before_next_sec}
                  onChange={(e) =>
                    setQuestionEditor((prev) => ({
                      ...prev,
                      delay_before_next_sec:
                        e.target.value === "" ? "" : Number(e.target.value),
                    }))
                  }
                  min="0"
                  placeholder="Например, 3600 для 1 часа"
                />
              </label>
            </div>

            <div className="form-row">
              <label className="form-label">
                Следующая воронка (опционально)
                <select
                  className="form-input"
                  value={questionEditor.next_funnel_id}
                  onChange={(e) =>
                    setQuestionEditor((prev) => ({
                      ...prev,
                      next_funnel_id:
                        e.target.value === "" ? "" : Number(e.target.value),
                    }))
                  }
                >
                  <option value="">Не запускать другую воронку автоматически</option>
                  {funnelsList
                    .filter((f) => f.id !== Number(selectedFunnelForQuestion))
                    .map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.title}
                      </option>
                    ))}
                </select>
              </label>
            </div>

            {questionEditor.existingFiles.length > 0 && (
              <div className="form-row">
                <span className="form-label">Прикреплённые файлы</span>
                <div className="file-preview-list">
                  {questionEditor.existingFiles.map((fileName) => (
                    <button
                      key={fileName}
                      type="button"
                      className={`pill ${questionEditor.filesToDelete.includes(fileName) ? "pill-danger" : ""
                        }`}
                      onClick={() => toggleQuestionEditorFile(fileName)}
                    >
                      {fileName}{" "}
                      {questionEditor.filesToDelete.includes(fileName) ? "будет удалён" : "оставить"}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="form-row">
              <label className="form-label">
                Добавить новые файлы (до 10 с учётом уже прикреплённых)
                <input
                  type="file"
                  multiple
                  className="form-file"
                  onChange={handleQuestionEditorNewFiles}
                />
              </label>
              {questionEditor.newFiles.length > 0 && (
                <div className="file-preview-list">
                  {questionEditor.newFiles.map((file) => (
                    <span className="pill" key={file.name}>
                      {file.name}
                      <button
                        type="button"
                        className="pill-action"
                        onClick={() => removeQuestionEditorNewFile(file.name)}
                      >
                        Г—
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" className="primary-btn">
              Обновить сообщение
            </button>
          </form>
        </div>
      )}

      {/* Шаг 4. Кнопки */}
      <div className="data-block">
        <h3 className="data-title">4. Кнопки / варианты ответа</h3>
        <form onSubmit={handleCreateButton} className="form">
          <div className="form-row">
            <label className="form-label">
              Сообщение
              <select
                className="form-input"
                value={selectedQuestionForButton}
                onChange={(e) => setSelectedQuestionForButton(e.target.value)}
                disabled={
                  !selectedFunnelForQuestion || questionsForButtonsSelect.length === 0
                }
              >
                {!selectedFunnelForQuestion ? (
                  <option value="">
                    Сначала выберите воронку выше
                  </option>
                ) : (
                  <>
                    <option value="">Выберите сообщение</option>
                    {questionsForButtonsSelect.map((q) => (
                      <option key={q.id} value={q.id}>
                        #{q.order_index ?? "?"} В· id={q.id} В·{" "}
                        {getQuestionPreview(q, 40)}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </label>
            {!selectedFunnelForQuestion && (
              <p className="hint">
                Чтобы прикрепить кнопку, сначала выберите воронку в шаге 3.
              </p>
            )}
          </div>
          {selectedQuestionForButton && (
            <>
              <div className="form-row">
                <label className="form-label">
                  Текст кнопки
                  <input
                    className="form-input"
                    value={buttonText}
                    onChange={(e) => setButtonText(e.target.value)}
                    required
                  />
                </label>
              </div>
              <div className="form-row">
                <label className="form-label">
                  Тип
                  <select
                    className="form-input"
                    value={buttonType}
                    onChange={(e) => setButtonType(e.target.value)}
                  >
                    <option value="buttons">Кнопка</option>
                    <option value="text">Текст</option>
                  </select>
                </label>
              </div>
              <div className="form-row">
                <label className="form-label">
                  URL (если нужно перейти по ссылке)
                  <input
                    className="form-input"
                    value={buttonUrl}
                    onChange={(e) => setButtonUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </label>
              </div>
              <div className="form-row">
                <label className="form-label">
                  Следующее сообщение
                  <select
                    className="form-input"
                    value={selectedNextQuestion}
                    onChange={(e) => setSelectedNextQuestion(e.target.value)}
                  >
                    <option value="">Выберите шаг</option>
                    <option value="0">Остановить воронку</option>
                    {nextQuestionsOptions.map((q) => (
                      <option key={q.id} value={q.id}>
                        [{q.funnelTitle}] В· #{q.order_index ?? "?"} В· id={q.id} В·{" "}
                        {getQuestionPreview(q, 60)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <button type="submit" className="primary-btn">
                Добавить кнопку
              </button>
            </>
          )}
        </form>

        {selectedQuestionForButton && (
          <>
            {paginatedButtons.length === 0 ? (
              <p className="hint" style={{ marginTop: "16px" }}>
                Пока нет кнопок
              </p>
            ) : (
              <div className="table" style={{ marginTop: "16px" }}>
                <div className="table-row table-header">
                  <div>Текст</div>
                  <div>Тип</div>
                  <div>Переход</div>
                  <div></div>
                </div>
                {paginatedButtons.map((btn) => (
                  <div className="table-row" key={btn.id}>
                    <div>{btn.text}</div>
                    <div>{btn.answer_type || btn.type}</div>
                    <div>{btn.next_question_id || "Конец"}</div>
                    <div>
                      <button
                        type="button"
                        className="ghost-btn"
                        onClick={() => startEditButton(btn)}
                      >
                        Редактировать
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {buttonsSorted.length > buttonsPerPage && (
              <PaginationControls
                page={buttonsPage}
                totalPages={buttonsTotalPages}
                onPrev={() => setButtonsPage((prev) => Math.max(1, prev - 1))}
                onNext={() =>
                  setButtonsPage((prev) => Math.min(buttonsTotalPages, prev + 1))
                }
              />
            )}
          </>
        )}
      </div>

      {buttonEditor && (
        <div className="card editor-card">
          <div className="editor-header">
            <h4>Редактировать кнопку #{buttonEditor.id}</h4>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setButtonEditor(null)}
            >
              Отмена
            </button>
          </div>
          <form onSubmit={handleSubmitButtonEdit} className="form">
            <div className="form-row">
              <label className="form-label">
                Текст
                <input
                  className="form-input"
                  value={buttonEditor.text}
                  onChange={(e) =>
                    setButtonEditor((prev) => ({ ...prev, text: e.target.value }))
                  }
                  required
                />
              </label>
            </div>
            <div className="form-row">
              <label className="form-label">
                Тип
                <select
                  className="form-input"
                  value={buttonEditor.answer_type}
                  onChange={(e) =>
                    setButtonEditor((prev) => ({ ...prev, answer_type: e.target.value }))
                  }
                >
                  <option value="buttons">Кнопка</option>
                  <option value="text">Текст</option>
                </select>
              </label>
            </div>
            <div className="form-row">
              <label className="form-label">
                URL
                <input
                  className="form-input"
                  value={buttonEditor.url || ""}
                  onChange={(e) =>
                    setButtonEditor((prev) => ({ ...prev, url: e.target.value }))
                  }
                  placeholder="Если нужна ссылка"
                />
              </label>
            </div>
            <div className="form-row">
              <label className="form-label">
                Следующее сообщение
                <select
                  className="form-input"
                  value={buttonEditor.next_question_id}
                  onChange={(e) =>
                    setButtonEditor((prev) => ({
                      ...prev,
                      next_question_id: Number(e.target.value),
                    }))
                  }
                >
                  <option value={0}>Конец воронки</option>
                  {nextQuestionsOptions.map((q) => (
                    <option key={q.id} value={q.id}>
                      [{q.funnelTitle}] В· #{q.order_index ?? "?"} В· id={q.id} В·{" "}
                      {getQuestionPreview(q, 60)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <button type="submit" className="primary-btn">
              Обновить кнопку
            </button>
          </form>
        </div>
      )}

      {/* Шаг 5. Сегменты */}
      <div className="data-block">
        <h3 className="data-title">5. Сегментация</h3>
        <div className="card-grid">
          <section className="card">
            <h4 className="card-title">Создать сегмент</h4>
            <form onSubmit={handleCreateSegment} className="form">
              <div className="form-row">
                <label className="form-label">
                  Название
                  <input
                    className="form-input"
                    value={segmentName}
                    onChange={(e) => setSegmentName(e.target.value)}
                    required
                  />
                </label>
              </div>
              <button type="submit" className="primary-btn">
                Сохранить
              </button>
            </form>
          </section>

          <section className="card">
            <h4 className="card-title">Правило сегмента</h4>
            <form onSubmit={handleCreateSegmentRule} className="form">
              <div className="form-row">
                <label className="form-label">
                  Сегмент
                  <select
                    className="form-input"
                    value={selectedSegmentForRule}
                    onChange={(e) => setSelectedSegmentForRule(e.target.value)}
                    required
                  >
                    <option value="">Выберите</option>
                    {segmentsList.map((segment) => (
                      <option key={segment.id} value={segment.id}>
                        {segment.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="form-row">
                <label className="form-label">
                  Кнопка
                  <select
                    className="form-input"
                    value={selectedAnswerForSegment}
                    onChange={(e) => setSelectedAnswerForSegment(e.target.value)}
                    required
                  >
                    <option value="">Выберите</option>
                    {allAnswerOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        #{opt.id} В· {opt.text}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <button type="submit" className="primary-btn">
                Привязать
              </button>
            </form>
          </section>
        </div>

        {segmentsWithRules.length > 0 ? (
          <>
            <div className="segment-grid">
              {paginatedSegments.map((segment) => (
                <div key={segment.id} className="segment-card">
                  <div className="segment-card-header">
                    <span className="segment-name">{segment.name}</span>
                    <span className="segment-id">ID: {segment.id}</span>
                  </div>
                  {segment.rules.length === 0 ? (
                    <p className="hint">Пока без правил</p>
                  ) : (
                    <ul className="segment-rules">
                      {segment.rules.map((rule) => (
                        <li key={rule.id}>
                          {rule.answerOption ? (
                            <>
                              <b>{rule.answerOption.text}</b>
                              {rule.question && (
                                <span className="hint">
                                  {" "}
                                  В· {getQuestionPreview(rule.question, 40)}
                                </span>
                              )}
                              {rule.funnel && (
                                <span className="hint"> В· [{rule.funnel.title}]</span>
                              )}
                            </>
                          ) : (
                            <span className="hint">answer_id: {rule.answer_id}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            {segmentsWithRules.length > segmentsPerPage && (
              <PaginationControls
                page={segmentsPage}
                totalPages={segmentsTotalPages}
                onPrev={() => setSegmentsPage((prev) => Math.max(1, prev - 1))}
                onNext={() =>
                  setSegmentsPage((prev) => Math.min(segmentsTotalPages, prev + 1))
                }
              />
            )}
          </>
        ) : (
          <p className="hint">Создайте сегмент, чтобы увидеть список</p>
        )}
      </div>

      {/* Шаг 6. Запуск и планирование */}
      <div className="data-block">
        <h3 className="data-title">6. Запуск воронки / расписание</h3>
        <form onSubmit={handleStartFunnel} className="form">
          <div className="form-row">
            <label className="form-label">
              Воронка
              <select
                className="form-input"
                value={selectedFunnelForStart}
                onChange={(e) => setSelectedFunnelForStart(e.target.value)}
                required
              >
                <option value="">Выберите</option>
                {funnelsList.map((funnel) => (
                  <option key={funnel.id} value={funnel.id}>
                    {funnel.title}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              Сегмент (опционально)
              <select
                className="form-input"
                value={selectedSegmentForStart}
                onChange={(e) => setSelectedSegmentForStart(e.target.value)}
              >
                <option value="">Всем пользователям</option>
                {segmentsList.map((segment) => (
                  <option key={segment.id} value={segment.id}>
                    {segment.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              Отложить отправку (UTC)
              <input
                type="datetime-local"
                className="form-input"
                value={startSentAtLocal}
                onChange={(e) => setStartSentAtLocal(e.target.value)}
              />
            </label>
          </div>
          <div className="form-row inline">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isTestBroadcast}
                onChange={(e) => setIsTestBroadcast(e.target.checked)}
              />
              Тестовая рассылка (только админам)
            </label>
          </div>
          <button type="submit" className="primary-btn">
            {isTestBroadcast ? "Отправить тест" : "Запустить / запланировать"}
          </button>
        </form>

        {showConfirmDialog && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={() => setShowConfirmDialog(false)}
          >
            <div
              className="card"
              style={{
                maxWidth: "400px",
                margin: "20px",
                cursor: "default",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="card-title" style={{ color: "var(--danger)" }}>
                ⚠️ Подтверждение рассылки
              </h3>
              <p style={{ marginBottom: "16px" }}>
                Вы собираетесь отправить рассылку <b>всем пользователям</b>.
                <br />
                <br />
                Вы уверены?
              </p>
              <div className="button-row">
                <button
                  type="button"
                  className="danger-btn"
                  onClick={() => executeBroadcast(pendingBroadcastData)}
                >
                  Да, отправить
                </button>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => {
                    setShowConfirmDialog(false);
                    setPendingBroadcastData(null);
                  }}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="card-grid" style={{ marginTop: "24px" }}>
          <section className="card">
            <div className="data-block-header">
              <h4 className="card-title">Отложенные рассылки</h4>
              <button
                type="button"
                className="secondary-btn small-btn"
                onClick={loadAllData}
                disabled={loading}
              >
                {loading ? "..." : "Обновить"}
              </button>
            </div>
            {paginatedUpcoming.length === 0 ? (
              <p className="hint">Нет запланированных задач</p>
            ) : (
              <>
                <div className="table">
                  <div className="table-row table-header">
                    <div>ID</div>
                    <div>Статус</div>
                    <div>Запланировано</div>
                    <div>Отправлено</div>
                    <div></div>
                  </div>
                  {paginatedUpcoming.map((item) => (
                    <div className="table-row" key={item.broadcast_id}>
                      <div>{item.broadcast_id.substring(0, 8)}...</div>
                      <div>{item.status}</div>
                      <div>
                        {item.send_at ? formatDateTime(item.send_at) : "—"}
                        {editingBroadcast === item.broadcast_id && (
                          <div style={{ marginTop: "8px" }}>
                            <input
                              type="datetime-local"
                              className="form-input"
                              style={{ fontSize: "11px", padding: "4px" }}
                              value={newBroadcastTime}
                              onChange={(e) => setNewBroadcastTime(e.target.value)}
                            />
                            <div className="button-row" style={{ marginTop: "4px" }}>
                              <button
                                type="button"
                                className="ghost-btn"
                                style={{ fontSize: "11px", padding: "2px 6px" }}
                                onClick={() => handleUpdateBroadcastTime(item.broadcast_id)}
                              >
                                Сохранить
                              </button>
                              <button
                                type="button"
                                className="ghost-btn"
                                style={{ fontSize: "11px", padding: "2px 6px" }}
                                onClick={() => {
                                  setEditingBroadcast(null);
                                  setNewBroadcastTime("");
                                }}
                              >
                                Отмена
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        {item.sent_count}/{item.total_users}
                      </div>
                      <div>
                        {editingBroadcast !== item.broadcast_id &&
                          item.status === "scheduled" && (
                            <div className="button-row" style={{ gap: "4px" }}>
                              <button
                                type="button"
                                className="ghost-btn"
                                style={{ fontSize: "11px", padding: "2px 6px" }}
                                onClick={() => {
                                  setEditingBroadcast(item.broadcast_id);
                                  if (item.send_at) {
                                    const date = new Date(item.send_at);
                                    const localDate = new Date(
                                      date.getTime() - date.getTimezoneOffset() * 60000
                                    );
                                    setNewBroadcastTime(
                                      localDate.toISOString().slice(0, 16)
                                    );
                                  }
                                }}
                              >
                                Изменить
                              </button>
                              <button
                                type="button"
                                className="ghost-btn"
                                style={{
                                  fontSize: "11px",
                                  padding: "2px 6px",
                                  color: "var(--danger)",
                                }}
                                onClick={() => handleCancelBroadcast(item.broadcast_id)}
                              >
                                Отменить
                              </button>
                            </div>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
                {upcomingBroadcasts.length > scheduledPerPage && (
                  <PaginationControls
                    page={scheduledUpcomingPage}
                    totalPages={upcomingTotalPages}
                    onPrev={() =>
                      setScheduledUpcomingPage((prev) => Math.max(1, prev - 1))
                    }
                    onNext={() =>
                      setScheduledUpcomingPage((prev) => Math.min(upcomingTotalPages, prev + 1))
                    }
                  />
                )}
              </>
            )}
          </section>

          <section className="card">
            <h4 className="card-title">Выполненные рассылки</h4>
            {paginatedHistory.length === 0 ? (
              <p className="hint">История пуста</p>
            ) : (
              <>
                <div className="table">
                  <div className="table-row table-header">
                    <div>ID</div>
                    <div>Старт</div>
                    <div>Завершено</div>
                    <div>Отправлено</div>
                  </div>
                  {paginatedHistory.map((item) => (
                    <div className="table-row" key={item.broadcast_id}>
                      <div>{item.broadcast_id}</div>
                      <div>{item.start_time ? formatDateTime(item.start_time) : "—"}</div>
                      <div>{item.end_time ? formatDateTime(item.end_time) : "—"}</div>
                      <div>
                        {item.sent_count}/{item.total_users}
                      </div>
                    </div>
                  ))}
                </div>
                {completedBroadcasts.length > scheduledPerPage && (
                  <PaginationControls
                    page={scheduledHistoryPage}
                    totalPages={historyTotalPages}
                    onPrev={() =>
                      setScheduledHistoryPage((prev) => Math.max(1, prev - 1))
                    }
                    onNext={() =>
                      setScheduledHistoryPage((prev) => Math.min(historyTotalPages, prev + 1))
                    }
                  />
                )}
              </>
            )}
          </section>
        </div>
      </div>

      {/* Модалка подтверждения удаления воронки */}
      {showDeleteFunnelDialog && funnelToDelete && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={cancelDeleteFunnel}
        >
          <div
            className="card"
            style={{
              maxWidth: "400px",
              margin: "20px",
              cursor: "default",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="card-title" style={{ color: "var(--danger)" }}>
              ⚠️ Удалить воронку?
            </h3>
            <p style={{ marginBottom: "16px" }}>
              Вы собираетесь удалить воронку{" "}
              <b>{funnelToDelete.title}</b> (ID {funnelToDelete.id}).
              <br />
              <br />
              Это действие <b>необратимо</b>. Удалятся все сообщения и кнопки
              внутри неё.
            </p>
            <div className="button-row">
              <button
                type="button"
                className="danger-btn"
                onClick={confirmDeleteFunnel}
              >
                Да, удалить
              </button>
              <button
                type="button"
                className="secondary-btn"
                onClick={cancelDeleteFunnel}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ======== EVENTS SECTION ========

function EventsSection({ apiRequest, apiDownloadBlob, setLastAction, showToast }) {
  const [eventsData, setEventsData] = useState([]);
  const [usersEvents, setUsersEvents] = useState([]);
  const [usersEventsFilter, setUsersEventsFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [newEventName, setNewEventName] = useState("");
  const [newEventMessage, setNewEventMessage] = useState("");
  const [newEventRegistrationText, setNewEventRegistrationText] = useState("");
  const [newEventDateTime, setNewEventDateTime] = useState("");
  const [newEventPhoto, setNewEventPhoto] = useState(null);
  const [createStatus, setCreateStatus] = useState("");
  const [updateEventName, setUpdateEventName] = useState("");
  const [updateNewEventName, setUpdateNewEventName] = useState("");
  const [updateEventMessage, setUpdateEventMessage] = useState("");
  const [updateEventRegistrationText, setUpdateEventRegistrationText] = useState("");
  const [updateEventDateTime, setUpdateEventDateTime] = useState("");
  const [updateEventPhoto, setUpdateEventPhoto] = useState(null);
  const [updateStatus, setUpdateStatus] = useState("");
  const [deleteEventName, setDeleteEventName] = useState("");
  const [deleteStatus, setDeleteStatus] = useState("");
  const [broadcastName, setBroadcastName] = useState("");
  const [broadcastText, setBroadcastText] = useState("");
  const [broadcastEventName, setBroadcastEventName] = useState("");
  const [broadcastTime, setBroadcastTime] = useState("");
  const [broadcastPhoto, setBroadcastPhoto] = useState(null);
  const [upcomingMailings, setUpcomingMailings] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);

  const [reminderText, setReminderText] = useState("");
  const [eventReminders, setEventReminders] = useState([]);
  const [reminderEventId, setReminderEventId] = useState("");
  const [reminderDaysBefore, setReminderDaysBefore] = useState("1");
  const [reminderRuleTime, setReminderRuleTime] = useState("10:00");
  const [reminderRulesLoading, setReminderRulesLoading] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderStatus, setReminderStatus] = useState("");
  const [reminderNextAt, setReminderNextAt] = useState("");
  const [reminderNextEta, setReminderNextEta] = useState("");
  const [eventsPage, setEventsPage] = useState(1);
  const [usersEventsPage, setUsersEventsPage] = useState(1);
  const eventsPerPage = 10;
  const usersEventsPerPage = 10;

  const truncate = (text, maxLen = 80) => {
    if (!text) return "—";
    if (text.length <= maxLen) return text;
    return `${text.slice(0, maxLen)}...`;
  };

  const formatArray = (arr, max = 5) => {
    if (!Array.isArray(arr) || arr.length === 0) return "—";
    const head = arr.slice(0, max).join(", ");
    if (arr.length <= max) return head;
    return `${head} и ещё ${arr.length - max}`;
  };

  const formatAvgRating = (ratings) => {
    if (!Array.isArray(ratings) || ratings.length === 0) return "—";
    const sum = ratings.reduce((acc, value) => acc + Number(value || 0), 0);
    const avg = sum / ratings.length;
    if (!Number.isFinite(avg)) return "—";
    return avg.toFixed(1);
  };

  const formatEventDate = (value) => {
    if (!value) return "";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatEventDates = (values) => {
    if (!Array.isArray(values) || values.length === 0) return "—";
    const formatted = values
      .map((value) => formatEventDate(value))
      .filter(Boolean)
      .join(", ");
    return formatted || "—";
  };

  const normalizeEventNames = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === "string") {
      return value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }
    return [];
  };

  const compareById = (a, b) => {
    const aId = Number(a?.id);
    const bId = Number(b?.id);
    if (Number.isNaN(aId) && Number.isNaN(bId)) return 0;
    if (Number.isNaN(aId)) return 1;
    if (Number.isNaN(bId)) return -1;
    return aId - bId;
  };

  const eventNameOptions = (() => {
    const names = new Set();
    for (const item of eventsData || []) {
      if (item?.event_name) {
        names.add(String(item.event_name));
      }
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  })();

  const eventById = (() => {
    const map = new Map();
    for (const item of eventsData || []) {
      if (item?.id != null) {
        map.set(String(item.id), item);
      }
    }
    return map;
  })();

  const sortedEventsData = [...(eventsData || [])].sort(compareById);

  const filteredUsersEventsRaw = usersEventsFilter
    ? usersEvents.filter((item) =>
        normalizeEventNames(item?.event_names).includes(usersEventsFilter)
      )
    : usersEvents;
  const filteredUsersEvents = [...filteredUsersEventsRaw].sort(compareById);

  const eventsTotalPages = Math.max(
    1,
    Math.ceil(sortedEventsData.length / eventsPerPage)
  );
  const usersEventsTotalPages = Math.max(
    1,
    Math.ceil(filteredUsersEvents.length / usersEventsPerPage)
  );

  const pagedEvents = sortedEventsData.slice(
    (eventsPage - 1) * eventsPerPage,
    eventsPage * eventsPerPage
  );
  const pagedUsersEvents = filteredUsersEvents.slice(
    (usersEventsPage - 1) * usersEventsPerPage,
    usersEventsPage * usersEventsPerPage
  );

  const clampPage = (page, total) => Math.min(Math.max(page, 1), total);

  const loadEventsData = async () => {
    setLoading(true);
    try {
      setLastAction("Загрузка мероприятий");
      const data = await apiRequest("/api/admin/events/data", {
        method: "GET",
        needsAuth: true,
        successMessage: false,
      });
      if (Array.isArray(data)) {
        setEventsData(data);
      } else if (Array.isArray(data?.events_data)) {
        setEventsData(data.events_data);
      } else {
        setEventsData([]);
      }

      const usersData = await apiRequest("/api/admin/events/users-events", {
        method: "GET",
        needsAuth: true,
        successMessage: false,
      });
      if (Array.isArray(usersData)) {
        setUsersEvents(usersData);
      } else if (Array.isArray(usersData?.users_events)) {
        setUsersEvents(usersData.users_events);
      } else {
        setUsersEvents([]);
      }
    } catch (e) {
      console.error("Ошибка загрузки мероприятий:", e);
    } finally {
      setLoading(false);
    }
  };

  const downloadEventExport = async (eventId) => {
    if (!eventId) return;
    // In Telegram mini app, opening in external browser loses auth headers/cookies.
    // Ask backend for a short-lived one-time download link.
    try {
      setLastAction("Выгрузка XLSX");
      // Add cache buster to avoid any intermediate caching of one-time links.
      const data = await apiRequest(
        `/api/admin/events/export-link/${eventId}?t=${Date.now()}`,
        {
        method: "GET",
        needsAuth: true,
        successMessage: false,
        }
      );
      const url = data?.url;
      if (!url) {
        showToast("Не удалось получить ссылку", "error");
        return;
      }
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error("Ошибка выгрузки XLSX:", e);
    }
  };

  useEffect(() => {
    setEventsPage((prev) => clampPage(prev, eventsTotalPages));
  }, [eventsTotalPages]);

  useEffect(() => {
    setUsersEventsPage((prev) => clampPage(prev, usersEventsTotalPages));
  }, [usersEventsTotalPages]);

  useEffect(() => {
    setUsersEventsPage(1);
  }, [usersEventsFilter]);

  const loadUpcomingMailings = async () => {
    setUpcomingLoading(true);
    try {
      const data = await apiRequest("/api/admin/mailing/upcoming", {
        method: "GET",
        needsAuth: true,
        successMessage: false,
      });
      if (Array.isArray(data)) {
        setUpcomingMailings(data);
      } else {
        setUpcomingMailings([]);
      }
    } catch (e) {
      console.error("Ошибка загрузки рассылок:", e);
      setUpcomingMailings([]);
    } finally {
      setUpcomingLoading(false);
    }
  };

  const loadReminderSettings = async () => {
    setReminderLoading(true);
    try {
      setLastAction("Загрузка настроек напоминаний");
      const data = await apiRequest("/api/admin/reminders/settings", {
        method: "GET",
        needsAuth: true,
        successMessage: false,
      });
      if (data) {
        if (data.reminder_text !== undefined && data.reminder_text !== null) {
          setReminderText(String(data.reminder_text));
        } else {
          setReminderText("");
        }
        if (data.next_reminder_at) {
          setReminderNextAt(String(data.next_reminder_at));
        } else {
          setReminderNextAt("");
        }
        if (data.next_reminder_eta) {
          setReminderNextEta(String(data.next_reminder_eta));
        } else {
          setReminderNextEta("");
        }
      }
    } catch (e) {
      console.error("Ошибка загрузки настроек напоминаний:", e);
    } finally {
      setReminderLoading(false);
    }
  };

  const loadEventReminders = async () => {
    setReminderRulesLoading(true);
    try {
      const data = await apiRequest("/api/admin/reminders/rules", {
        method: "GET",
        needsAuth: true,
        successMessage: false,
      });
      if (Array.isArray(data)) {
        setEventReminders(data);
      } else {
        setEventReminders([]);
      }
    } catch (e) {
      console.error("Ошибка загрузки напоминаний:", e);
      setEventReminders([]);
    } finally {
      setReminderRulesLoading(false);
    }
  };

  useEffect(() => {
    loadEventsData();
    loadUpcomingMailings();
    loadReminderSettings();
    loadEventReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setLastAction("Создать мероприятие");
    const formData = new FormData();
    formData.append("event_name", newEventName.trim());
    formData.append("message_text", newEventMessage.trim());
    formData.append("registration_text", newEventRegistrationText.trim());
    formData.append("date_time", newEventDateTime);
    if (newEventPhoto) {
      formData.append("photo", newEventPhoto);
    }

    try {
      const data = await apiRequest("/api/admin/events/create", {
        method: "POST",
        needsAuth: true,
        body: formData,
        isFormData: true,
        successMessage: "Мероприятие создано",
      });
      if (data?.event_id) {
        setCreateStatus(`Создано. Event ID: ${data.event_id}`);
      } else {
        setCreateStatus("Мероприятие создано");
      }
      setNewEventName("");
      setNewEventMessage("");
      setNewEventRegistrationText("");
      setNewEventDateTime("");
      setNewEventPhoto(null);
      loadEventsData();
    } catch (e) {
      setCreateStatus("Не удалось создать мероприятие");
      console.error("Ошибка создания мероприятия:", e);
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    setLastAction("Изменить мероприятие");
    try {
      const formData = new FormData();
      formData.append("event_name", updateEventName.trim());
      formData.append("new_event_name", updateNewEventName.trim());
      formData.append("message_text", updateEventMessage.trim());
      formData.append("registration_text", updateEventRegistrationText.trim());
      formData.append("date_time", updateEventDateTime);
      if (updateEventPhoto) {
        formData.append("photo", updateEventPhoto);
      }
      await apiRequest("/api/admin/events/update-by-name", {
        method: "PUT",
        needsAuth: true,
        body: formData,
        isFormData: true,
        successMessage: "Мероприятие обновлено",
      });
      setUpdateStatus("Мероприятие обновлено");
      setUpdateEventName("");
      setUpdateNewEventName("");
      setUpdateEventMessage("");
      setUpdateEventRegistrationText("");
      setUpdateEventDateTime("");
      setUpdateEventPhoto(null);
      loadEventsData();
    } catch (e) {
      setUpdateStatus("Не удалось обновить мероприятие");
      console.error("Ошибка обновления мероприятия:", e);
    }
  };

  const handleDeleteEvent = async (e) => {
    e.preventDefault();
    setLastAction("Удалить мероприятие");
    try {
      await apiRequest("/api/admin/events/delete-by-name", {
        method: "DELETE",
        needsAuth: true,
        body: { event_name: deleteEventName.trim() },
        successMessage: "Мероприятие удалено",
      });
      setDeleteStatus("Мероприятие удалено");
      setDeleteEventName("");
      loadEventsData();
    } catch (e) {
      setDeleteStatus("Не удалось удалить мероприятие");
      console.error("Ошибка удаления мероприятия:", e);
    }
  };

  const handleCreateBroadcast = async (e) => {
    e.preventDefault();
    setLastAction("Создать рассылку");
    const formData = new FormData();
    formData.append("title", broadcastName.trim());
    formData.append("message_text", broadcastText.trim());
    if (broadcastEventName) {
      formData.append("event_name", broadcastEventName);
    }
    if (broadcastTime) {
      formData.append("send_at", broadcastTime);
    }
    if (broadcastPhoto) {
      formData.append("photo", broadcastPhoto);
    }
    try {
      await apiRequest("/api/admin/mailing/create", {
        method: "POST",
        needsAuth: true,
        body: formData,
        isFormData: true,
        successMessage: "Рассылка создана",
      });
      setBroadcastName("");
      setBroadcastText("");
      setBroadcastEventName("");
      setBroadcastTime("");
      setBroadcastPhoto(null);
      await loadUpcomingMailings();
    } catch (e) {
      console.error("Ошибка создания рассылки:", e);
    }
  };

  const handleDeleteMailing = async (mailingId) => {
    if (!window.confirm("Удалить рассылку?")) return;
    setLastAction("Удалить рассылку");
    try {
      await apiRequest(`/api/admin/mailing/${mailingId}`, {
        method: "DELETE",
        needsAuth: true,
        successMessage: "Рассылка удалена",
      });
      await loadUpcomingMailings();
    } catch (e) {
      console.error("Ошибка удаления рассылки:", e);
    }
  };

  const handleSaveReminders = async (e) => {
    e.preventDefault();
    setReminderStatus("");

    if (!reminderText.trim()) {
      showToast("Введите текст напоминания", "error");
      return;
    }

    setReminderLoading(true);
    try {
      setLastAction("Сохранение настроек напоминаний");
      const data = await apiRequest("/api/admin/reminders/settings", {
        method: "PUT",
        needsAuth: true,
        body: {
          reminder_text: reminderText,
        },
      });
      if (data) {
        setReminderStatus("Настройки сохранены");
        if (data.next_reminder_at) {
          setReminderNextAt(String(data.next_reminder_at));
        } else {
          setReminderNextAt("");
        }
        if (data.next_reminder_eta) {
          setReminderNextEta(String(data.next_reminder_eta));
        } else {
          setReminderNextEta("");
        }
      }
    } catch (e) {
      console.error("Ошибка сохранения настроек:", e);
    } finally {
      setReminderLoading(false);
    }
  };

  const handleAddReminderRule = async (e) => {
    e.preventDefault();
    const days = Number(reminderDaysBefore);
    if (!reminderEventId) {
      showToast("Выберите мероприятие", "error");
      return;
    }
    if (!Number.isInteger(days) || days < 1) {
      showToast("Количество дней должно быть >= 1", "error");
      return;
    }
    if (!reminderRuleTime) {
      showToast("Укажите время напоминания", "error");
      return;
    }

    try {
      setLastAction("Добавить напоминание");
      await apiRequest("/api/admin/reminders/rules", {
        method: "POST",
        needsAuth: true,
        body: {
          event_id: Number(reminderEventId),
          days_before: days,
          send_time: reminderRuleTime,
        },
        successMessage: "Напоминание добавлено",
      });
      setReminderDaysBefore("1");
      setReminderRuleTime("10:00");
      await loadEventReminders();
    } catch (e) {
      console.error("Ошибка добавления напоминания:", e);
    }
  };

  const handleDeleteReminderRule = async (ruleId) => {
    if (!window.confirm("Удалить напоминание?")) return;
    try {
      setLastAction("Удалить напоминание");
      await apiRequest(`/api/admin/reminders/rules/${ruleId}`, {
        method: "DELETE",
        needsAuth: true,
        successMessage: "Напоминание удалено",
      });
      await loadEventReminders();
    } catch (e) {
      console.error("Ошибка удаления напоминания:", e);
    }
  };

  return (
    <>
      <div className="data-block-header" style={{ marginBottom: "16px" }}>
        <h2 className="section-title">Мероприятия</h2>
        <button
          type="button"
          className="secondary-btn small-btn"
          onClick={loadEventsData}
          disabled={loading}
        >
          {loading ? "Загрузка..." : "Обновить"}
        </button>
      </div>
      <p className="section-description">
        Данные из таблиц events_data и users_events.
      </p>

      <section className="card">
        <h3 className="card-title">Создать мероприятие</h3>
        <form onSubmit={handleCreateEvent} className="form">
          <div className="form-row">
            <label className="form-label">
              Название
              <input
                type="text"
                className="form-input"
                value={newEventName}
                onChange={(e) => setNewEventName(e.target.value)}
                required
                placeholder="Название мероприятия"
              />
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              Текст сообщения
              <textarea
                className="form-textarea"
                value={newEventMessage}
                onChange={(e) => setNewEventMessage(e.target.value)}
                required
                rows={4}
                placeholder="Сообщение для участников"
              />
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              Текст регистрации
              <textarea
                className="form-textarea"
                value={newEventRegistrationText}
                onChange={(e) => setNewEventRegistrationText(e.target.value)}
                required
                rows={3}
                placeholder="Благодарим за регистрацию! Встречаемся {дата}."
              />
            </label>
            <p className="hint">
              Поддерживаются шаблоны <code>{`{название}`}</code> и{" "}
              <code>{`{дата}`}</code>.
            </p>
          </div>
          <div className="form-row">
            <label className="form-label">
              Дата и время
              <input
                type="datetime-local"
                className="form-input"
                value={newEventDateTime}
                onChange={(e) => setNewEventDateTime(e.target.value)}
                required
              />
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              Фото
              <input
                type="file"
                className="form-input"
                accept="image/*"
                onChange={(e) =>
                  setNewEventPhoto(e.target.files && e.target.files[0])
                }
              />
            </label>
            {newEventPhoto && (
              <p className="hint">Выбрано: {newEventPhoto.name}</p>
            )}
          </div>
          <div className="button-row">
            <button
              type="submit"
              className="primary-btn"
              disabled={
                !newEventName.trim() ||
                !newEventMessage.trim() ||
                !newEventRegistrationText.trim() ||
                !newEventDateTime
              }
            >
              Создать
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => {
                setNewEventName("");
                setNewEventMessage("");
                setNewEventRegistrationText("");
                setNewEventDateTime("");
                setNewEventPhoto(null);
                setCreateStatus("");
              }}
            >
              Очистить
            </button>
          </div>
          {createStatus && <p className="hint">{createStatus}</p>}
          {newEventPhoto && <p className="hint">Фото: {newEventPhoto.name}</p>}
        </form>
      </section>

      <section className="card">
        <h3 className="card-title">Изменить мероприятие по названию</h3>
        <form onSubmit={handleUpdateEvent} className="form">
          <div className="form-row">
            <label className="form-label">
              Текущее название
              <input
                type="text"
                className="form-input"
                value={updateEventName}
                onChange={(e) => setUpdateEventName(e.target.value)}
                required
                placeholder="Существующее название"
              />
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              Новое название
              <input
                type="text"
                className="form-input"
                value={updateNewEventName}
                onChange={(e) => setUpdateNewEventName(e.target.value)}
                required
                placeholder="Новое название"
              />
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              Текст сообщения
              <textarea
                className="form-textarea"
                value={updateEventMessage}
                onChange={(e) => setUpdateEventMessage(e.target.value)}
                required
                rows={4}
                placeholder="Новое сообщение для участников"
              />
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              Текст регистрации
              <textarea
                className="form-textarea"
                value={updateEventRegistrationText}
                onChange={(e) => setUpdateEventRegistrationText(e.target.value)}
                required
                rows={3}
                placeholder="Текст после регистрации с {название} и {дата}"
              />
            </label>
            <p className="hint">
              Поддерживаются шаблоны <code>{`{название}`}</code> и{" "}
              <code>{`{дата}`}</code>.
            </p>
          </div>
          <div className="form-row">
            <label className="form-label">
              Дата и время
              <input
                type="datetime-local"
                className="form-input"
                value={updateEventDateTime}
                onChange={(e) => setUpdateEventDateTime(e.target.value)}
                required
              />
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              Фото
              <input
                type="file"
                className="form-input"
                accept="image/*"
                onChange={(e) =>
                  setUpdateEventPhoto(e.target.files && e.target.files[0])
                }
              />
            </label>
            {updateEventPhoto && (
              <p className="hint">Выбрано: {updateEventPhoto.name}</p>
            )}
          </div>
          <div className="button-row">
            <button
              type="submit"
              className="primary-btn"
              disabled={
                !updateEventName.trim() ||
                !updateNewEventName.trim() ||
                !updateEventMessage.trim() ||
                !updateEventRegistrationText.trim() ||
                !updateEventDateTime
              }
            >
              Изменить
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => {
                setUpdateEventName("");
                setUpdateNewEventName("");
                setUpdateEventMessage("");
                setUpdateEventRegistrationText("");
                setUpdateEventDateTime("");
                setUpdateEventPhoto(null);
                setUpdateStatus("");
              }}
            >
              Очистить
            </button>
          </div>
          {updateStatus && <p className="hint">{updateStatus}</p>}
        </form>
      </section>

      <section className="card">
        <h3 className="card-title">Удалить мероприятие по названию</h3>
        <form onSubmit={handleDeleteEvent} className="form">
          <div className="form-row">
            <label className="form-label">
              Название мероприятия
              <input
                type="text"
                className="form-input"
                value={deleteEventName}
                onChange={(e) => setDeleteEventName(e.target.value)}
                required
                placeholder="Название для удаления"
              />
            </label>
          </div>
          <div className="button-row">
            <button
              type="submit"
              className="primary-btn"
              disabled={!deleteEventName.trim()}
            >
              Удалить
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => {
                setDeleteEventName("");
                setDeleteStatus("");
              }}
            >
              Очистить
            </button>
          </div>
          {deleteStatus && <p className="hint">{deleteStatus}</p>}
        </form>
      </section>

      <section className="card">
        <h3 className="card-title">Рассылка</h3>
        <form onSubmit={handleCreateBroadcast} className="form">
          <div className="form-row">
            <label className="form-label">
              Название рассылки
              <input
                type="text"
                className="form-input"
                value={broadcastName}
                onChange={(e) => setBroadcastName(e.target.value)}
                required
                placeholder="Название рассылки"
              />
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              Мероприятие (критерий)
              <select
                className="form-input"
                value={broadcastEventName}
                onChange={(e) => setBroadcastEventName(e.target.value)}
              >
                <option value="">Все мероприятия</option>
                {eventNameOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              Текст сообщения
              <textarea
                className="form-textarea"
                value={broadcastText}
                onChange={(e) => setBroadcastText(e.target.value)}
                required
                rows={4}
                placeholder="Сообщение для рассылки"
              />
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              Дата и время отправки
              <input
                type="datetime-local"
                className="form-input"
                value={broadcastTime}
                onChange={(e) => setBroadcastTime(e.target.value)}
              />
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">
              Фото
              <input
                type="file"
                className="form-input"
                accept="image/*"
                onChange={(e) =>
                  setBroadcastPhoto(e.target.files && e.target.files[0])
                }
              />
            </label>
            {broadcastPhoto && (
              <p className="hint">Выбрано: {broadcastPhoto.name}</p>
            )}
          </div>
          <div className="button-row">
            <button
              type="submit"
              className="primary-btn"
              disabled={!broadcastName.trim() || !broadcastText.trim()}
            >
              Создать рассылку
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => {
                setBroadcastName("");
                setBroadcastText("");
                setBroadcastEventName("");
                setBroadcastTime("");
                setBroadcastPhoto(null);
              }}
            >
              Очистить
            </button>
          </div>
        </form>

        <div style={{ marginTop: "16px" }}>
          <div className="data-block-header" style={{ marginBottom: "8px" }}>
            <h4 className="data-title" style={{ margin: 0 }}>
              Предстоящие рассылки
            </h4>
            <button
              type="button"
              className="secondary-btn small-btn"
              onClick={loadUpcomingMailings}
              disabled={upcomingLoading}
            >
              {upcomingLoading ? "Загрузка..." : "Обновить"}
            </button>
          </div>
          {upcomingLoading ? (
            <p className="hint">Загрузка рассылок...</p>
          ) : upcomingMailings.length === 0 ? (
            <p className="hint">Нет запланированных рассылок</p>
          ) : (
            <div className="table">
              <div className="table-row table-header">
                <div>ID</div>
                <div>Название</div>
                <div>Мероприятие</div>
                <div>Время</div>
                <div></div>
              </div>
              {upcomingMailings.map((item) => (
                <div className="table-row" key={item.id}>
                  <div>{item.id}</div>
                  <div>{item.title || "—"}</div>
                  <div>{item.event_name || "—"}</div>
                  <div>{formatDateTime(item.send_at)}</div>
                  <div>
                    <button
                      type="button"
                      className="ghost-btn"
                      style={{ color: "var(--danger)" }}
                      onClick={() => handleDeleteMailing(item.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-title-row">
          <h3 className="card-title">Напоминания о мероприятиях</h3>
          <button
            type="button"
            className="secondary-btn small-btn"
            onClick={() => {
              loadReminderSettings();
              loadEventReminders();
            }}
            disabled={reminderLoading}
          >
            Обновить
          </button>
        </div>
        <p className="hint">
          Для каждого мероприятия можно задать несколько напоминаний.
        </p>
        <p className="hint">
          Текст напоминания можно настроить ниже. Поддерживаются шаблоны{" "}
          <code>{`{название}`}</code> и <code>{`{дата}`}</code>.
        </p>
        {(reminderNextAt || reminderNextEta) && (
          <p className="hint">
            {reminderNextEta
              ? reminderNextEta
              : `Следующее напоминание в ${formatDateTime(reminderNextAt)}`}
          </p>
        )}
        <form onSubmit={handleSaveReminders}>
          <div className="form-row">
            <label className="form-label">
              Текст напоминания
              <textarea
                className="form-textarea"
                value={reminderText}
                onChange={(e) => setReminderText(e.target.value)}
                rows={3}
                placeholder="Напоминаем, что вы записаны на мероприятие {название}, которое состоится {дата}."
              />
            </label>
          </div>
          <div className="button-row">
            <button type="submit" className="primary-btn" disabled={reminderLoading}>
              Сохранить
            </button>
          </div>
          {reminderStatus && <p className="hint">{reminderStatus}</p>}
        </form>

        <div style={{ marginTop: "10px" }}>
          <h4 className="data-title">Добавить напоминание</h4>
          <form onSubmit={handleAddReminderRule} className="form">
            <div className="form-row">
              <label className="form-label">
                Мероприятие
                <select
                  className="form-input"
                  value={reminderEventId}
                  onChange={(e) => setReminderEventId(e.target.value)}
                  required
                >
                  <option value="">Выберите мероприятие</option>
                  {(eventsData || []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.event_name || `ID ${item.id}`}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="form-row">
              <label className="form-label">
                За сколько дней до мероприятия
                <input
                  type="number"
                  className="form-input"
                  value={reminderDaysBefore}
                  onChange={(e) => setReminderDaysBefore(e.target.value)}
                  min={1}
                  required
                />
              </label>
            </div>
            <div className="form-row">
              <label className="form-label">
                Время напоминания
                <input
                  type="time"
                  className="form-input"
                  value={reminderRuleTime}
                  onChange={(e) => setReminderRuleTime(e.target.value)}
                  required
                />
              </label>
            </div>
            <div className="button-row">
              <button type="submit" className="primary-btn">
                Добавить
              </button>
            </div>
          </form>
        </div>

        <div style={{ marginTop: "12px" }}>
          <h4 className="data-title">Список напоминаний</h4>
          {reminderRulesLoading ? (
            <p className="hint">Загрузка...</p>
          ) : eventReminders.length === 0 ? (
            <p className="hint">Напоминаний нет</p>
          ) : (
            <div className="table">
              <div className="table-row table-header">
                <div>Мероприятие</div>
                <div>За сколько дней</div>
                <div>Время</div>
                <div></div>
              </div>
              {eventReminders.map((rule) => {
                const event = eventById.get(String(rule.event_id));
                return (
                  <div className="table-row" key={rule.id}>
                    <div>{event?.event_name || `ID ${rule.event_id}`}</div>
                    <div>{rule.days_before}</div>
                    <div>{rule.send_time}</div>
                    <div>
                      <button
                        type="button"
                        className="ghost-btn"
                        style={{ color: "var(--danger)" }}
                        onClick={() => handleDeleteReminderRule(rule.id)}
                        title="Удалить"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-title-row">
          <h3 className="card-title">Мероприятия</h3>
        </div>
        <div className="hint">Всего мероприятий: {eventsData.length}</div>
        {loading ? (
          <p className="hint">Загрузка...</p>
        ) : eventsData.length === 0 ? (
          <p className="hint">Данные отсутствуют</p>
        ) : (
          <>
            <div className="table">
              <div className="table-row table-header events-table-row">
                <div>ID</div>
                <div>Название</div>
                <div>Event ID</div>
                <div>Ссылка</div>
                <div>Даты</div>
                <div>Записаны</div>
                <div>Средний балл</div>
                <div>Пользователи</div>
                <div>Сообщение</div>
                <div>Регистрация</div>
              </div>
              {pagedEvents.map((item) => (
                <div className="table-row events-table-row" key={item.id}>
                  <div>{item.id}</div>
                  <div>{item.event_name || "—"}</div>
                  <div>{item.event_id || "—"}</div>
                  <div>
                    {buildEventLink(item.event_id) ? (
                      <div className="link-cell">
                        <span className="link-text">
                          {buildEventLink(item.event_id)}
                        </span>
                        <button
                          type="button"
                          className="ghost-btn"
                          onClick={async () => {
                            const ok = await copyToClipboard(
                              buildEventLink(item.event_id)
                            );
                            showToast(
                              ok ? "Ссылка скопирована" : "Не удалось скопировать",
                              ok ? "success" : "error"
                            );
                          }}
                        >
                          Копировать
                        </button>
                        <button
                          type="button"
                          className="ghost-btn"
                          onClick={() => downloadEventExport(item.event_id)}
                          title="Выгрузить регистрации и оценки"
                        >
                          XLSX
                        </button>
                      </div>
                    ) : (
                      "—"
                    )}
                  </div>
                  <div>{formatEventDates(item.date)}</div>
                  <div>
                    {Array.isArray(item.users)
                      ? item.users.filter(
                          (u) => u !== null && u !== undefined && u !== ""
                        ).length
                      : 0}
                  </div>
                  <div>{formatAvgRating(item.ratings)}</div>
                  <div>{formatArray(item.users)}</div>
                  <div>{truncate(item.message_text, 80)}</div>
                  <div>{truncate(item.registration_text, 80)}</div>
                </div>
              ))}
            </div>
            {eventsData.length > 0 && eventsTotalPages > 1 && (
              <div className="pagination-controls compact pagination-bottom">
                <button
                  type="button"
                  className="pagination-button"
                  onClick={() =>
                    setEventsPage((p) => clampPage(p - 1, eventsTotalPages))
                  }
                  disabled={eventsPage <= 1}
                >
                  ←
                </button>
                <span className="pagination-info">
                  {eventsPage} / {eventsTotalPages}
                </span>
                <button
                  type="button"
                  className="pagination-button"
                  onClick={() =>
                    setEventsPage((p) => clampPage(p + 1, eventsTotalPages))
                  }
                  disabled={eventsPage >= eventsTotalPages}
                >
                  →
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <section className="card">
        <div className="card-title-row">
          <h3 className="card-title">Пользователи</h3>
        </div>
        <div className="data-block-controls" style={{ marginBottom: "12px" }}>
          <label className="form-label" style={{ width: "100%" }}>
            Фильтр по мероприятию
            <select
              className="form-input"
              value={usersEventsFilter}
              onChange={(e) => setUsersEventsFilter(e.target.value)}
            >
              <option value="">Все мероприятия</option>
              {eventNameOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="hint">
          Всего пользователей: {usersEvents.length}
          {usersEventsFilter ? `, по фильтру: ${filteredUsersEvents.length}` : ""}
        </div>
        {loading ? (
          <p className="hint">Загрузка...</p>
        ) : usersEvents.length === 0 ? (
          <p className="hint">Данные отсутствуют</p>
        ) : (
          <>
            <div className="table">
              <div className="table-row table-header users-events-table-row">
              <div>ID</div>
              <div>Контакты</div>
              <div>Мероприятия</div>
            </div>
            {pagedUsersEvents.map((item) => (
              <div className="table-row users-events-table-row" key={item.id}>
                <div>{item.id}</div>
                <div className="users-events-contacts-cell">
                  {formatArray(item.contacts)}
                </div>
                <div>{formatArray(item.event_names)}</div>
              </div>
            ))}
            </div>
            {filteredUsersEvents.length > 0 && usersEventsTotalPages > 1 && (
              <div className="pagination-controls compact pagination-bottom">
                <button
                  type="button"
                  className="pagination-button"
                  onClick={() =>
                    setUsersEventsPage((p) =>
                      clampPage(p - 1, usersEventsTotalPages)
                    )
                  }
                  disabled={usersEventsPage <= 1}
                >
                  ←
                </button>
                <span className="pagination-info">
                  {usersEventsPage} / {usersEventsTotalPages}
                </span>
                <button
                  type="button"
                  className="pagination-button"
                  onClick={() =>
                    setUsersEventsPage((p) =>
                      clampPage(p + 1, usersEventsTotalPages)
                    )
                  }
                  disabled={usersEventsPage >= usersEventsTotalPages}
                >
                  →
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}
// ======== SUPPORT SECTION ========

function SupportSection({ apiRequest, setLastAction }) {
  const [answerTicketId, setAnswerTicketId] = useState("");
  const [answerText, setAnswerText] = useState("");
  const [tickets, setTickets] = useState([]);
  const [ticketPreview, setTicketPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ticketSearch, setTicketSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | open | answered
  const [ticketsPage, setTicketsPage] = useState(1);
  const ticketsPerPage = 10;

  const PaginationControls = ({ page, totalPages, onPrev, onNext }) => (
    <div className="pagination-controls compact">
      <button
        type="button"
        className="pagination-button"
        onClick={onPrev}
        disabled={page <= 1}
      >
        ←
      </button>
      <span className="pagination-info">
        {page} / {totalPages}
      </span>
      <button
        type="button"
        className="pagination-button"
        onClick={onNext}
        disabled={page >= totalPages}
      >
        →
      </button>
    </div>
  );

  const loadTickets = async () => {
    setLoading(true);
    try {
      setLastAction("Загрузить тикеты");
      const data = await apiRequest("/api/admin/support/ticket/list", {
        method: "GET",
        needsAuth: true,
        successMessage: false,
      });
      if (Array.isArray(data)) {
        setTickets(data);
      }
    } catch (e) {
      console.error("Ошибка загрузки тикетов:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetTicketById = async (ticketId) => {
    if (!ticketId) return;
    try {
      setLastAction("Загрузить тикет");
      const data = await apiRequest(`/api/admin/support/ticket/${ticketId}`, {
        method: "GET",
        needsAuth: true,
        successMessage: false,
      });
      if (data) {
        setTicketPreview(data);
        setAnswerTicketId(String(ticketId));
      }
    } catch (e) {
      console.error("Ошибка загрузки тикета:", e);
      setTicketPreview(null);
    }
  };

  const handleAnswerTicketAsAdmin = async (e) => {
    e.preventDefault();
    setLastAction("Ответить на тикет");
    await apiRequest("/api/admin/support/ticket/answer", {
      method: "POST",
      needsAuth: true,
      body: {
        ticket_id: Number(answerTicketId),
        answer_text: answerText,
      },
      successMessage: "Ответ отправлен",
    });
    setAnswerTicketId("");
    setAnswerText("");
    setTicketPreview(null);
    await loadTickets();
  };

  useEffect(() => {
    if (answerTicketId && /^\d+$/.test(answerTicketId)) {
      handleGetTicketById(Number(answerTicketId));
    } else {
      setTicketPreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerTicketId]);

  const filteredTickets = tickets.filter((ticket) => {
    if (statusFilter === "open" && ticket.status) return false;
    if (statusFilter === "answered" && !ticket.status) return false;
    const searchLower = ticketSearch.toLowerCase();
    const ticketId = ticket.ticket_id || ticket.id;
    return (
      String(ticketId).includes(searchLower) ||
      String(ticket.user_id).includes(searchLower) ||
      (ticket.question_text || "").toLowerCase().includes(searchLower)
    );
  });

  const paginatedTickets = (() => {
    if (!Array.isArray(filteredTickets) || ticketsPerPage <= 0) return [];
    const start = (ticketsPage - 1) * ticketsPerPage;
    return filteredTickets.slice(start, start + ticketsPerPage);
  })();

  const ticketsTotalPages = Math.max(
    1,
    Math.ceil((filteredTickets.length || 1) / ticketsPerPage)
  );

  return (
    <>
      <div className="data-block-header" style={{ marginBottom: "16px" }}>
        <h2 className="section-title">Поддержка</h2>
        <button
          type="button"
          onClick={loadTickets}
          className="secondary-btn small-btn"
          disabled={loading}
        >
          {loading ? "Загрузка..." : "Обновить"}
        </button>
      </div>
      <p className="section-description">
        Управление тикетами поддержки от пользователей.
      </p>

      <div className="card-grid">
        <section className="card" style={{ gridColumn: "1 / -1" }}>
          <h3 className="card-title">Тикеты</h3>
          <div className="data-block-controls" style={{ marginBottom: "12px" }}>
            <select
              className="form-input"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setTicketsPage(1);
              }}
              style={{ maxWidth: "220px" }}
            >
              <option value="all">Все</option>
              <option value="open">Не отвечено</option>
              <option value="answered">Отвечено</option>
            </select>
            <input
              type="text"
              className="search-input"
              value={ticketSearch}
              onChange={(e) => {
                setTicketSearch(e.target.value);
                setTicketsPage(1);
              }}
              placeholder="Поиск по ID, пользователю, тексту..."
            />
          </div>
          {loading ? (
            <p className="hint">Загрузка тикетов...</p>
          ) : filteredTickets.length === 0 ? (
            <p className="hint">
              {ticketSearch ? "Тикеты не найдены" : "Тикетов нет"}
            </p>
          ) : (
            <>
              <div className="table">
                <div className="table-row table-header">
                  <div>ID</div>
                  <div>Пользователь</div>
                  <div>Статус</div>
                  <div>Вопрос</div>
                  <div>Дата</div>
                  <div></div>
                </div>
                {paginatedTickets.map((ticket) => {
                  const ticketId = ticket.ticket_id || ticket.id;
                  const previewId = ticketPreview?.ticket_id || ticketPreview?.id;
                  const isAnswered = Boolean(ticket.status);
                  return (
                    <div
                      className="table-row"
                      key={ticketId}
                      style={{
                        cursor: "pointer",
                        opacity: previewId === ticketId ? 0.7 : 1,
                      }}
                      onClick={() => handleGetTicketById(ticketId)}
                    >
                      <div>
                        <b>#{ticketId}</b>
                      </div>
                      <div>{ticket.user_id}</div>
                      <div>
                        <span
                          className={`badge ${
                            isAnswered ? "badge-ok" : "badge-warn"
                          }`}
                        >
                          {isAnswered ? "Отвечено" : "Не отвечено"}
                        </span>
                      </div>
                      <div>
                        {(ticket.question_text || "").substring(0, 60)}
                        {(ticket.question_text || "").length > 60 ? "..." : ""}
                      </div>
                      <div>{formatDateTime(ticket.created_at)}</div>
                      <div>
                        {!isAnswered && (
                          <button
                            type="button"
                            className="ghost-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGetTicketById(ticketId);
                            }}
                          >
                            Ответить
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {filteredTickets.length > ticketsPerPage && (
                <PaginationControls
                  page={ticketsPage}
                  totalPages={ticketsTotalPages}
                  onPrev={() => setTicketsPage((prev) => Math.max(1, prev - 1))}
                  onNext={() =>
                    setTicketsPage((prev) => Math.min(ticketsTotalPages, prev + 1))
                  }
                />
              )}
            </>
          )}
        </section>

        <section className="card" style={{ gridColumn: "1 / -1" }}>
          <h3 className="card-title">Ответить на тикет</h3>
          <form onSubmit={handleAnswerTicketAsAdmin} className="form">
            <div className="form-row">
              <label className="form-label">
                ID тикета
                <input
                  type="number"
                  className="form-input"
                  value={answerTicketId}
                  onChange={(e) => setAnswerTicketId(e.target.value)}
                  required
                  placeholder="Введите ID или выберите из списка"
                />
              </label>
            </div>
            {ticketPreview && (
              <div className="data-block" style={{ marginBottom: "12px" }}>
                <h4 className="data-title">Диалог</h4>
                <p style={{ whiteSpace: "pre-wrap" }}>
                  <b>Вопрос:</b>{" "}
                  {ticketPreview.question_text || "(пусто)"}
                </p>
                {ticketPreview.answer_text && (
                  <p style={{ whiteSpace: "pre-wrap", marginTop: "10px" }}>
                    <b>Ответ:</b> {ticketPreview.answer_text}
                  </p>
                )}
                <p className="hint">
                  От пользователя {ticketPreview.user_id} ·{" "}
                  {formatDateTime(ticketPreview.created_at)}
                  {ticketPreview.answered_at
                    ? ` · Ответили: ${formatDateTime(ticketPreview.answered_at)}`
                    : ""}
                </p>
              </div>
            )}
            <div className="form-row">
              <label className="form-label">
                Ответ
                <textarea
                  className="form-textarea"
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  required={!ticketPreview?.status}
                  placeholder="Введите ответ пользователю"
                  rows={5}
                  disabled={Boolean(ticketPreview?.status)}
                />
              </label>
            </div>
            <button
              type="submit"
              className="primary-btn"
              disabled={
                !answerTicketId ||
                Boolean(ticketPreview?.status) ||
                !answerText.trim()
              }
            >
              Отправить ответ
            </button>
          </form>
        </section>
      </div>
    </>
  );
}

export default App;
